import {
    ClientEvent,
    createClient,
    Direction,
    EventType,
    Method,
    MatrixClient,
    MatrixError,
    MatrixEvent,
    MsgType,
    Room,
    RoomEvent,
    RoomNameType,
    ISearchResponse,
    ISearchRequestBody,
    SearchOrderBy,
    SyncState,
    StateEvents,
    MatrixEventEvent,
} from 'matrix-js-sdk';
import { PushRuleKind, PushRuleActionName, ConditionKind } from 'matrix-js-sdk/lib/@types/PushRules';
import { HistoryVisibility, Preset } from 'matrix-js-sdk/lib/@types/partials';
import type { ICreateRoomOpts } from 'matrix-js-sdk/lib/@types/requests';
import type { ICreateClientOpts } from 'matrix-js-sdk/lib/matrix';
import type { CryptoApi, CryptoCallbacks, KeyBackupCheck } from 'matrix-js-sdk/lib/crypto-api';
import type { SecretStorageKeyDescription } from 'matrix-js-sdk/lib/secret-storage';
import type { RoomMessageEventContent } from 'matrix-js-sdk/lib/@types/events';
import type { UploadOpts } from 'matrix-js-sdk/lib/http-api/interface';
import { CryptoEvent, deriveRecoveryKeyFromPassphrase, VerificationRequest } from 'matrix-js-sdk/lib/crypto-api';
import {
    CallEvent,
    CallState,
    CallErrorCode,
    CallType,
    type MatrixCall,
} from "matrix-js-sdk/lib/webrtc/call";
import { IndexedDBStore } from 'matrix-js-sdk/lib/store/indexeddb';
import { IndexedDBCryptoStore } from 'matrix-js-sdk/lib/crypto/store/indexeddb-crypto-store';
import { ChatLoginResponse } from "./chatServiceTypes.ts";
import {chatApi} from "./chatService.ts";
import {RoomPreviewPrefetcher} from "./roomPreviewPrefetcher.ts";
import {
    formatSearchResults,
    isRelevantTimelineEvent,
    isSummaryMessageContent,
    isTranscriptionMessageContent
} from "../utilis/chatUtils.ts";
import {
    buildCallTimelineEntries,
    type CallTimelineEntry,
    type CallTimelineEvent,
    isCallInviteEvent,
    isCallTimelineEvent,
} from "../utilis/callTimeline.ts";
import {isRtcNotificationEvent} from "../utilis/rtcNotification.ts";

import logger from "./logger.ts";
import {store} from "../store/store.ts";
import {setMatrixSession} from "../store/features/Auth/authSlice.ts";
import {slidingSyncManager} from "./slidingSyncManager.ts";
import {MultiInviter} from "../utils/MultiInviter.ts";
import { getStoredDeviceId, persistDeviceId, removeDeviceId } from "../utils/deviceIdStorage.ts";
import { setChatStorageItem } from "../utils/chatStorage.ts";
import {
    getMatrixDeviceIdRecoveryCandidate,
    MatrixDeviceIdMismatchError,
} from './matrixDeviceIdRecovery.ts';
import { checkOwnDeviceKeys } from './matrixOwnDeviceKeys.ts';
import { removeMatrixToken } from "../utils/matrixTokenStorage.ts";
import { mediaCacheService } from "./mediaCacheService.ts";
import {
    getBotUserId,
    isBotUser,
    isServiceParticipant,
    buildRoomName,
    buildEmptyRoomName,
    setAiBotSession,
    createGroupPowerLevels,
    createDirectRoomPowerLevels,
} from "../utils/aiBotUtils.ts";
import { encryptMatrixAttachment } from "../utils/encryptedMedia.ts";
import type { MatrixEncryptedFile } from "../utils/encryptedMedia.ts";
import { generateImageThumbnail, THUMBNAIL_MIMETYPE } from "../utils/imageThumbnail.ts";
import { generateVideoThumbnail } from "../utils/videoThumbnail.ts";
import type { VideoThumbnail } from "../utils/videoThumbnail.ts";
import {formatMediaCaptionWithMentions} from "../utils/mentionUtils.ts";
import {buildReplyMessageContent, buildTextMessageContent} from "../utils/messageFormatting.ts";
import {buildPrivateReplyQuote, composePrivateReplyMessage, escapeHtml, type PrivateReplySource} from "../utils/privateReplyQuote.ts";
import {buildEditContent, isEditableMessage, isMessageEdit, resolveEditTargetEventId} from "../utils/messageEdits.ts";
import {authenticatedFetch} from "./authRuntime";
import {getApiUrl} from "../config";
import {releaseMatrixSessionLock} from "./matrixSessionLock";
import type {MatrixSessionLockState} from "./matrixSessionLock";
import { CallEventHandlerEvent } from "matrix-js-sdk/lib/webrtc/callEventHandler";
import { ActiveCallEventFastLane } from "./matrixCalling/ActiveCallEventFastLane";
import { isValidCallRingingContent, MATRIX_CALL_RINGING_EVENT_TYPE } from "./matrixCalling/callRinging";
import { MATRIX_CALL_ESCALATE_EVENT_TYPE } from "./matrixCalling/callEscalation";
import { releaseRejectedMediaRequests } from "./matrixCalling/mediaRequestGuard";
import {VoiceHandler} from "../components/Chat/VoiceCallHandler.tsx";
import {RTC_PROTOCOL_V1} from "@phonehq/rtc-core";
import {
    setDialerHidden,
    setIncomingCall
} from "../store/features/calls/dialerSlice.ts";
import {
    MATRIX_CALL_METADATA_EVENT_TYPE,
    readMatrixCallMetadataContent,
    setCallMetadata
} from "../store/features/MatrixEvents/MatrixEventsService.tsx";
import {createRoomKickError} from "./roomKickError.ts";
import {createAiBotRoomError} from "./aiBotRoomError.ts";

import {buildAiRequest, buildAiStatusRequest, isSummaryPeriod, newAiRequestMetadata} from '../utils/aiRequestProtocol';
import {deliverAiRequest} from '../utils/aiRequestTransport';
import type {AiRequestContent, AiRequestKind, AiRequestMetadata} from '../utils/aiRequestProtocol';

const THUMBNAIL_BUILD_TIMEOUT_MS = 10000;

const ROOM_STATE_EVENTS_REQUIRING_UI_REFRESH = new Set<string>([
    EventType.RoomAvatar,
    EventType.RoomMember,
    EventType.RoomName,
    EventType.RoomPowerLevels,
    EventType.RoomEncryption,
    EventType.RoomTopic,
]);

export const shouldRefreshRoomState = (eventType: string): boolean =>
    ROOM_STATE_EVENTS_REQUIRING_UI_REFRESH.has(eventType);

enum GroupKey {
    RoomId = "room_id",
    Sender = "sender",
}

enum Status {
    NotStarted = 'not_started',
    InProgress = 'in_progress',
    Complete = 'complete',
    Error = 'error',
}

interface PassphraseResponse {
    passphrase: string;
}

interface MatrixRefreshTokenResponse {
    access_token: string;
    refresh_token?: string;
    expires_in_ms?: number;
}

interface MatrixTokenRefreshResult {
    accessToken: string;
    refreshToken?: string;
    expiresInMs: number | null;
}

interface MatrixTokenSession {
    readonly client: MatrixClient;
    readonly generation: number;
    readonly signal: AbortSignal;
}

const MATRIX_TOKEN_REFRESH_LIFETIME_RATIO = 0.75;
const MATRIX_TOKEN_REFRESH_CHECK_INTERVAL_MS = 60_000;
const MATRIX_TOKEN_REFRESH_RETRY_BASE_MS = 30_000;
const MATRIX_TOKEN_REFRESH_RETRY_MAX_MS = 5 * 60_000;
const MATRIX_TOKEN_ISSUING_PATH = /\/_matrix\/client\/.+\/(refresh|login)$/;

const parseUrl = (value: string): URL | null => {
    try {
        return new URL(value);
    } catch {
        return null;
    }
};

const bearerTokenOf = (init?: RequestInit): string | null => {
    const authorization = new Headers(init?.headers).get('Authorization');
    return authorization?.startsWith('Bearer ') ? authorization.substring(7) : null;
};

const withBearerToken = (init: RequestInit | undefined, accessToken: string): RequestInit => {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);
    return {...init, headers};
};

const isTokenRefreshableRequest = (client: MatrixClient, input: RequestInfo | URL): boolean => {
    const url = parseUrl(input instanceof Request ? input.url : String(input));
    const homeserverUrl = parseUrl(client.getHomeserverUrl());
    if (!url || !homeserverUrl) return false;
    return url.origin === homeserverUrl.origin && !MATRIX_TOKEN_ISSUING_PATH.test(url.pathname);
};

const isUnknownTokenResponse = async (response: Response): Promise<boolean> => {
    if (response.status !== 401) return false;
    try {
        const body: unknown = JSON.parse(await response.clone().text());
        return typeof body === 'object' && body !== null && 'errcode' in body && body.errcode === 'M_UNKNOWN_TOKEN';
    } catch {
        return false;
    }
};

interface AiBotRoomRequest {
    readonly roomId: string;
    readonly operationPath: 'invite' | 'policy';
    readonly method: 'POST' | 'PUT';
}

export interface AiBotRoomOperationResponse {
    readonly membership?: 'INVITED' | 'ALREADY_PRESENT';
    readonly policy: 'UPDATED' | 'UNCHANGED';
}

export type DeviceKeysStatus = 'idle' | 'loading' | 'ready' | 'partial';

export interface DeviceKeysProgress {
    total: number;
    loaded: number;
    failed: number;
}

export interface ChatState {
    isInitialized: boolean;
    isAuthenticated: boolean;
    /**
     * True once the first sync has landed and the room list is whole. Unread
     * totals climb as rooms stream in before that, so anything showing a count
     * should wait for this rather than render a number that keeps changing.
     */
    hasCompletedInitialSync: boolean;
    initialRoomRestoreSettled: boolean;
    sessionLockState: MatrixSessionLockState;
    currentUserId: string | null;
    selectedRoomId: string | null;
    rooms: Room[];
    syncState: string;
    error: string | null;
    isCryptoEnabled: boolean;
    isDeviceVerified: boolean;
    hasSecretStorage: boolean;
    crossSigningStatus: Status;
    backupStatus: Status;
    useSlidingSync: boolean;
    clientReady: boolean;
    cryptoReady: boolean;
    deviceKeysStatus: DeviceKeysStatus;
    deviceKeysProgress: DeviceKeysProgress;
}

export interface CryptoRuntimeStatus {
    serverVersion: string | null;
    activeVersion: string | null;
    privateKeyPresent: boolean;
    trustedSignature: boolean;
    matchesDecryptionKey: boolean;
    usable: boolean;
    serverSessionCount: number | null;
    crossSigningReady: boolean;
    secretStorageReady: boolean;
    secretStoragePbkdf2Configured: boolean;
    secretStoragePbkdf2Iterations: number | null;
    secretStorageKeyCached: boolean;
    secretStorageCachedKeyMatches: boolean | null;
    backupStatus: Status;
}

export interface SearchOptions {
    query: string;
    roomIds?: string | string[];
    limit?: number;
    orderBy?: SearchOrderBy;
    nextBatch?: string;
    includeState?: boolean;
    eventTypes?: string[];
    senders?: string[];
    beforeLimit?: number;
    afterLimit?: number;
}

export type DirectMessageRoomOutcome =
    | {status: 'found'; roomId: string}
    | {status: 'pending'; roomId?: string}
    | {status: 'not-found'};

export interface LocalRoomUser {
    userId: string;
    displayName: string;
    avatarUrl?: string;
}

export interface LocalRoom {
    localId: string;
    name?: string;
    otherUsers: LocalRoomUser[];
    createdAt: number;
    isDirect: boolean;
}

export interface LastMessageOverride {
    roomId: string;
    content: string;
    timestamp: number;
    sender: string;
    senderDisplayName: string;
    type: 'text' | 'image' | 'file' | 'voice' | 'other';
}

interface ContextEvent {
    eventId: string;
    sender: string;
    timestamp: number;
    type: string;
    body: string;
    msgtype?: string;
}

interface SearchContext {
    eventsBefore: ContextEvent[];
    eventsAfter: ContextEvent[];
    start?: string;
    end?: string;
}

interface FormattedSearchResult {
    eventId: string;
    roomId: string;
    sender: string;
    timestamp: number;
    body: string;
    msgtype: string;
    formattedBody?: string;
    rank?: number;
    context: SearchContext;
    profile: Record<string, any>;
}

interface SearchResponse {
    results: FormattedSearchResult[];
    count: number;
    nextBatch?: string;
    highlights: string[];
    groups: Record<string, any>;
}

interface ChatMediaThumbnailInfo {
    w: number;
    h: number;
    mimetype: string;
    size: number;
}

interface ChatMediaInfo {
    size: number;
    mimetype: string;
    w?: number;
    h?: number;
    duration?: number;
    thumbnail_url?: string;
    thumbnail_file?: MatrixEncryptedFile;
    thumbnail_info?: ChatMediaThumbnailInfo;
}

class MatrixSessionSupersededError extends Error {
    constructor() {
        super('Matrix session was superseded');
        this.name = 'MatrixSessionSupersededError';
    }
}

export class MatrixCryptoStoreEvictedError extends Error {
    readonly code = 'MATRIX_CRYPTO_STORE_EVICTED';

    constructor() {
        super('Encryption storage was reset by the browser. Creating a new Matrix device.');
        this.name = 'MatrixCryptoStoreEvictedError';
    }
}

interface UnpublishedDeviceStores {
    readonly storeId: string;
    readonly cryptoDbPrefix: string;
    readonly tenant: string;
    readonly authUserId: string;
    readonly matrixUserId: string;
}

/**
 * The server has no (or foreign) identity keys for this device, while the
 * local crypto store believes they were uploaded. Recovered like an evicted
 * store: drop the store and log in with a new device, which uploads fresh keys.
 */
export class MatrixOwnDeviceKeysMissingError extends MatrixCryptoStoreEvictedError {
    constructor(
        readonly status: 'missing' | 'mismatch',
        readonly stores: UnpublishedDeviceStores,
    ) {
        super();
        this.name = 'MatrixOwnDeviceKeysMissingError';
        this.message = 'This device has no encryption keys on the server. Creating a new Matrix device.';
    }
}

export class MatrixService {
    private client: MatrixClient | null = null;
    private initStage = 'idle';
    private lastInitDevice: {tenant: string; userId: string; deviceId: string} | null = null;
    private eventCallbacks: Map<string, Function[]> = new Map();
    private chatState: ChatState = {
        isInitialized: false,
        isAuthenticated: false,
        hasCompletedInitialSync: false,
        initialRoomRestoreSettled: false,
        sessionLockState: 'active',
        currentUserId: null,
        selectedRoomId: null,
        rooms: [],
        syncState: 'STOPPED',
        error: null,
        isCryptoEnabled: false,
        isDeviceVerified: false,
        hasSecretStorage: false,
        crossSigningStatus: Status.NotStarted,
        backupStatus: Status.NotStarted,
        useSlidingSync: true,
        clientReady: false,
        cryptoReady: false,
        deviceKeysStatus: 'idle',
        deviceKeysProgress: { total: 0, loaded: 0, failed: 0 },
    };

    private processingRooms: Set<string> = new Set();
    private deviceId: string | null = null;
    private existingRoomIds: Set<string> = new Set();
    private cachedSecretStorageKey: { keyId: string; key: Uint8Array } | null = null;
    private downloadingKeys: Map<string, Promise<void>> = new Map();
    private downloadedKeys: Set<string> = new Set();
    private roomsInSetup: Set<string> = new Set();
    private roomsPendingEncryption: Set<string> = new Set();
    private roomPendingMemberIds: Map<string, Set<string>> = new Map();
    // Users invited at room creation. Used to suppress join/invite notifications
    // for the initial cohort. Self-cleans as members join; bounded by a TTL.
    private roomInitialInviteeIds: Map<string, Set<string>> = new Map();
    private localRooms: LocalRoom[] = [];
    private localToRealRoomMap: Map<string, string> = new Map();
    private lastMessageOverrides: Map<string, LastMessageOverride> = new Map();
    private roomsListUpdateTimeout: number | null = null;
    private pendingRoomDataCount = 0;
    private sessionGeneration = 0;
    private sessionAbortController: AbortController | null = null;
    private matrixRefreshToken: string | null = null;
    private tokenRefreshTimeoutId: ReturnType<typeof window.setTimeout> | null = null;
    private tokenRefreshInFlight: Promise<MatrixTokenRefreshResult> | null = null;
    private tokenRefreshDueAtMs: number | null = null;
    private tokenRefreshFailures = 0;
    private detachTokenRefreshWakeListeners: (() => void) | null = null;
    private tokenExpiryRecoveryInFlight: Promise<void> | null = null;
    private matrixTokenExpiresAtMs: number | null = null;
    private rejectedAccessToken: string | null = null;
    private initializationInFlight: {
        generation: number;
        promise: Promise<void>;
    } | null = null;
    private logoutInFlight: Promise<void> | null = null;

    private activeCalls: Map<string, MatrixCall> = new Map();
    private incomingCalls: Map<string, MatrixCall> = new Map();

    private static readonly SELECTED_ROOM_STORAGE_KEY = 'matrix-selected-room-id';
    private explicitRoomSelected = false;
    private openedRoomIds = new Set<string>();
    private roomSelectionSeq = 0;
    private previewPrefetchRefreshTimeout: number | null = null;
    private voiceHandler = new VoiceHandler();

    private encCtx(extra?: Record<string, any>) {
        return {
            source: 'encryption',
            deviceId: this.deviceId || 'unknown',
            ...extra,
        };
    }

    private toLoggableJson(value: unknown): unknown {
        try {
            const seen = new WeakSet<object>();
            return JSON.parse(
                JSON.stringify(value, (_key, val) => {
                    if (typeof val === 'bigint') return val.toString();
                    if (typeof val === 'function') return `[Function ${val.name || 'anonymous'}]`;
                    if (val && typeof val === 'object') {
                        if (seen.has(val)) return '[Circular]';
                        seen.add(val);
                    }
                    return val;
                })
            );
        } catch (error) {
            return {
                serializationError: error instanceof Error ? error.message : String(error),
                preview: String(value),
            };
        }
    }

    private logRoomEventsDump(roomId: string, events: MatrixEvent[], returnedEvents?: MatrixEvent[]): void {
        if (import.meta.env.PROD) return;
        const returnedEventIds = new Set(returnedEvents?.map(event => event.getId()).filter(Boolean));
        const targetEvents = returnedEvents ?? events.slice(-50);
        const dumpedEvents = targetEvents.map((event, index) => {
            const content = event.getContent();
            const clearContent = event.getClearContent?.();
            const wireContent = event.getWireContent?.();
            const effectiveEvent = event.getEffectiveEvent?.();

            return {
                index,
                eventId: event.getId(),
                roomId: event.getRoomId(),
                sender: event.getSender(),
                timestamp: event.getTs(),
                type: event.getType(),
                wireType: event.getWireType?.(),
                isEncrypted: event.isEncrypted?.(),
                isDecryptionFailure: event.isDecryptionFailure?.(),
                isRedacted: event.isRedacted(),
                isReturnedMessage: returnedEventIds.has(event.getId()),
                msgtype: content?.msgtype,
                body: content?.body,
                filename: content?.filename,
                url: content?.url,
                file: content?.file,
                info: content?.info,
                content: this.toLoggableJson(content),
                clearContent: this.toLoggableJson(clearContent),
                wireContent: this.toLoggableJson(wireContent),
                effectiveEvent: this.toLoggableJson(effectiveEvent),
                rawEvent: this.toLoggableJson(event.event),
                unsigned: this.toLoggableJson(event.getUnsigned?.()),
            };
        });

        logger.info(`[WEB CHAT] Room ${roomId} event dump (${dumpedEvents.length} events)`, {
            source: 'chat',
            event: 'room-events-dump',
            roomId,
            eventCount: dumpedEvents.length,
            returnedEventCount: returnedEvents?.length ?? 0,
            events: dumpedEvents,
        });
    }

    private isSessionCurrent(generation: number, client?: MatrixClient): boolean {
        return generation === this.sessionGeneration && (!client || this.client === client);
    }

    private assertSessionCurrent(generation: number, client?: MatrixClient): void {
        if (!this.isSessionCurrent(generation, client)) {
            logger.info('[WEB CHAT] Ignoring superseded Matrix session work', this.encCtx({
                event: 'matrix_session_superseded',
                generation,
                currentGeneration: this.sessionGeneration,
            }));
            throw new MatrixSessionSupersededError();
        }
    }

    private createCryptoCallbacks(generation: number, signal: AbortSignal): CryptoCallbacks {
        return {
            getSecretStorageKey: async (
                opts: { keys: Record<string, SecretStorageKeyDescription> },
            ): Promise<[string, Uint8Array] | null> => {
                try {
                    this.assertSessionCurrent(generation);
                    const requestedKeyIds = Object.keys(opts.keys);
                    const defaultKeyId = await this.client?.secretStorage.getDefaultKeyId();
                    this.assertSessionCurrent(generation);
                    const keyId = defaultKeyId && opts.keys[defaultKeyId]
                        ? defaultKeyId
                        : requestedKeyIds[0];
                    if (!keyId) {
                        logger.error('[WEB CHAT] getSecretStorageKey: no keys provided in opts', this.encCtx({ event: 'secret_storage_no_keys' }));
                        return null;
                    }

                    logger.info('[WEB CHAT] getSecretStorageKey: selected Secret Storage key', this.encCtx({
                        event: 'secret_storage_key_selected',
                        keyId,
                        selectedDefaultKey: keyId === defaultKeyId,
                        requestedKeyCount: requestedKeyIds.length,
                    }));

                    if (this.cachedSecretStorageKey?.keyId === keyId) {
                        logger.info('[WEB CHAT] getSecretStorageKey: using cached key', this.encCtx({ event: 'secret_storage_cache_hit', keyId }));
                        return [keyId, this.cachedSecretStorageKey.key];
                    }

                    const keyInfo = opts.keys[keyId];
                    const passphraseInfo = keyInfo?.passphrase;
                    const hasValidPbkdf2 = passphraseInfo?.algorithm === 'm.pbkdf2'
                        && typeof passphraseInfo.salt === 'string'
                        && passphraseInfo.salt.length > 0
                        && Number.isSafeInteger(passphraseInfo.iterations)
                        && passphraseInfo.iterations > 0;

                    if (!hasValidPbkdf2) {
                        logger.error('[WEB CHAT] getSecretStorageKey: PBKDF2 metadata is missing or invalid', this.encCtx({
                            event: 'secret_storage_pbkdf2_missing',
                            keyId,
                            algorithm: passphraseInfo?.algorithm ?? null,
                            hasSalt: typeof passphraseInfo?.salt === 'string' && passphraseInfo.salt.length > 0,
                            iterations: Number.isFinite(passphraseInfo?.iterations) ? passphraseInfo?.iterations : null,
                        }));
                        return null;
                    }

                    logger.info('[WEB CHAT] getSecretStorageKey: deriving recovery key from passphrase', this.encCtx({
                        event: 'secret_storage_derive',
                        keyId,
                        hasPbkdf2: true,
                        iterations: passphraseInfo.iterations,
                    }));
                    const {passphrase} = await this.getPassphraseFromBackend(signal);
                    this.assertSessionCurrent(generation);
                    const recoveryKey = await deriveRecoveryKeyFromPassphrase(
                        passphrase,
                        passphraseInfo.salt,
                        passphraseInfo.iterations
                    );
                    this.assertSessionCurrent(generation);

                    const keyMatches = await this.client?.secretStorage.checkKey(recoveryKey, keyInfo);
                    this.assertSessionCurrent(generation);
                    if (!keyMatches) {
                        logger.error('[WEB CHAT] getSecretStorageKey: derived key does not match Secret Storage', this.encCtx({
                            event: 'secret_storage_key_mismatch',
                            keyId,
                        }));
                        return null;
                    }

                    this.cachedSecretStorageKey = {keyId, key: recoveryKey};
                    logger.info('[WEB CHAT] getSecretStorageKey: recovery key derived successfully', this.encCtx({ event: 'secret_storage_derived', keyId }));
                    return [keyId, recoveryKey];
                } catch (error) {
                    if (error instanceof MatrixSessionSupersededError || signal.aborted) {
                        return null;
                    }
                    logger.error(`[WEB CHAT] getSecretStorageKey failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                        event: 'secret_storage_error',
                        error: error instanceof Error ? error.message : String(error),
                    }));
                    return null;
                }
            },

            cacheSecretStorageKey: (keyId: string, _keyInfo: any, key: Uint8Array) => {
                if (this.isSessionCurrent(generation)) {
                    this.cachedSecretStorageKey = {keyId, key};
                }
            },
        };
    }

    private logUntrustedKeyBackup(
        backupVersion: string,
        trustInfo: { trusted: boolean; matchesDecryptionKey: boolean },
        context: Record<string, unknown> = {},
        activeBackupVersion: string | null = null,
    ): void {
        if (trustInfo.trusted || trustInfo.matchesDecryptionKey) return;

        logger.warn('[WEB CHAT] Key backup present on server but not trusted', this.encCtx({
            event: 'key_backup_not_trusted',
            ...context,
            backupVersion,
            activeBackupVersion,
            trusted: trustInfo.trusted,
            matchesDecryptionKey: trustInfo.matchesDecryptionKey,
        }));
    }

    private async checkKeyBackupAndEnableWithLogging(
        crypto: CryptoApi,
        context: Record<string, unknown> = {},
    ): Promise<KeyBackupCheck | null> {
        const result = await crypto.checkKeyBackupAndEnable();
        if (!result?.backupInfo) return result;

        let activeBackupVersion: string | null = null;
        if (!result.trustInfo.trusted && !result.trustInfo.matchesDecryptionKey) {
            try {
                activeBackupVersion = await crypto.getActiveSessionBackupVersion();
            } catch (error) {
                logger.warn('[WEB CHAT] Failed to read active key backup version', this.encCtx({
                    event: 'key_backup_active_version_error',
                    ...context,
                    error: error instanceof Error ? error.message : String(error),
                }));
            }
        }

        this.logUntrustedKeyBackup(
            String(result.backupInfo.version),
            result.trustInfo,
            context,
            activeBackupVersion,
        );
        return result;
    }

    private async waitForInitialSyncPrepared(
        client: MatrixClient,
        timeoutMs = 30_000,
        signal?: AbortSignal,
    ): Promise<void> {
        if (client.getSyncState() === SyncState.Prepared) return;
        if (signal?.aborted) throw new MatrixSessionSupersededError();

        await new Promise<void>((resolve, reject) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const cleanup = (): void => {
                if (timeoutId !== null) clearTimeout(timeoutId);
                client.off(ClientEvent.Sync, onSync);
                signal?.removeEventListener('abort', onAbort);
            };
            const onSync = (state: SyncState): void => {
                if (state !== SyncState.Prepared) return;
                cleanup();
                resolve();
            };
            const onAbort = (): void => {
                cleanup();
                reject(new MatrixSessionSupersededError());
            };

            timeoutId = setTimeout(() => {
                cleanup();
                reject(new Error(`Timed out after ${timeoutMs}ms waiting for initial Matrix sync`));
            }, timeoutMs);
            client.on(ClientEvent.Sync, onSync);
            signal?.addEventListener('abort', onAbort, {once: true});

            if (signal?.aborted) {
                onAbort();
            } else if (client.getSyncState() === SyncState.Prepared) {
                onSync(SyncState.Prepared);
            }
        });
    }

    async isFirstDeviceSetup(
        setupClient: MatrixClient | null = this.client,
        generation = this.sessionGeneration,
    ): Promise<boolean> {
        if (!setupClient) throw new Error('Client not initialized');
        this.assertSessionCurrent(generation, setupClient);

        const crypto = setupClient.getCrypto();
        if (!crypto) throw new Error('Crypto not available');

        const isSecretStorageReady = await crypto.isSecretStorageReady();
        this.assertSessionCurrent(generation, setupClient);
        return !isSecretStorageReady;
    }

    async setupFirstDevice(
        setupClient: MatrixClient | null = this.client,
        generation = this.sessionGeneration,
        signal?: AbortSignal,
    ): Promise<void> {
        logger.info('[WEB CHAT] setupFirstDevice: starting first device crypto setup', this.encCtx({ event: 'first_device_start' }));
        if (!setupClient) throw new Error('Client not initialized');
        this.assertSessionCurrent(generation, setupClient);

        try {
            this.chatState.crossSigningStatus = Status.InProgress;
            this.chatState.backupStatus = Status.InProgress;
            this.emitStateChange();

            const crypto = setupClient.getCrypto();
            if (!crypto) throw new Error('Crypto not available');

            const existingBackup = await crypto.getKeyBackupInfo();
            this.assertSessionCurrent(generation, setupClient);
            if (existingBackup) {
                logger.warn('[WEB CHAT] setupFirstDevice: backup already exists on server, treating as new device', this.encCtx({
                    event: 'first_device_has_backup',
                    backupVersion: existingBackup.version,
                }));
                return await this.setupNewDevice(setupClient, generation);
            }

            const {passphrase} = await this.getPassphraseFromBackend(signal);
            this.assertSessionCurrent(generation, setupClient);
            const recoveryKey = await crypto.createRecoveryKeyFromPassphrase(passphrase);
            this.assertSessionCurrent(generation, setupClient);


            this.cachedSecretStorageKey = {
                keyId: '',
                key: recoveryKey.privateKey
            };

            let crossSigningFailed = false;
            try {
                await crypto.bootstrapCrossSigning({
                    authUploadDeviceSigningKeys: async (makeRequest) => {
                        return makeRequest({});
                    },
                });
                logger.info('[WEB CHAT] setupFirstDevice: cross-signing bootstrap complete', this.encCtx({ event: 'first_device_cross_signing_done' }));
            } catch (csError: any) {
                logger.error(`[WEB CHAT] setupFirstDevice: cross-signing bootstrap failed (possible orphaned state): ${csError.message}`, this.encCtx({ event: 'first_device_cross_signing_failed', error: csError.message }));
                crossSigningFailed = true;
            }
            this.assertSessionCurrent(generation, setupClient);

            logger.info('[WEB CHAT] setupFirstDevice: bootstrapping secret storage', this.encCtx({ event: 'first_device_secret_storage_start' }));
            await crypto.bootstrapSecretStorage({
                createSecretStorageKey: async () => {
                    return recoveryKey;
                },
                setupNewSecretStorage: true,
                setupNewKeyBackup: true,
            });
            this.assertSessionCurrent(generation, setupClient);

            await this.storePBKDF2ParamsManually(setupClient, recoveryKey, generation);
            this.assertSessionCurrent(generation, setupClient);

            this.chatState.hasSecretStorage = true;

            if (crossSigningFailed) {
                // Secret storage + backup created, but cross-signing needs recovery.
                // Next login will route through setupNewDevice which can fix this.
                logger.warn('[WEB CHAT] setupFirstDevice: completed with degraded cross-signing — will recover on next session', this.encCtx({ event: 'first_device_degraded' }));
                this.chatState.crossSigningStatus = Status.Error;
                this.chatState.backupStatus = Status.Complete;
                this.emitStateChange();
                return;
            }

            // Verify backup was created and is trusted
            const backupInfo = await this.checkKeyBackupAndEnableWithLogging(crypto, {
                reason: 'first_device_setup',
            });
            this.assertSessionCurrent(generation, setupClient);

            if (!backupInfo?.backupInfo) {
                throw new Error('No backup found on server after setup');
            }

            const trustInfo = await crypto.isKeyBackupTrusted(backupInfo.backupInfo);
            this.assertSessionCurrent(generation, setupClient);
            if (!trustInfo.matchesDecryptionKey) {
                throw new Error('Backup key mismatch - encryption setup failed!');
            }

            logger.info('[WEB CHAT] setupFirstDevice: backup verified', this.encCtx({ event: 'first_device_backup_verified' }));
            this.chatState.crossSigningStatus = Status.Complete;
            this.chatState.backupStatus = Status.Complete;
            this.emitStateChange();

            logger.info('[WEB CHAT] setupFirstDevice: completed successfully', this.encCtx({ event: 'first_device_complete' }));

        } catch (error) {
            if (!this.isSessionCurrent(generation, setupClient)) throw error;
            logger.error(`[WEB CHAT] setupFirstDevice failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'first_device_error',
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
            }));
            this.chatState.crossSigningStatus = Status.Error;
            this.chatState.backupStatus = Status.Error;
            this.emitStateChange();
            throw error;
        }
    }

    async setupNewDevice(
        setupClient: MatrixClient | null = this.client,
        generation = this.sessionGeneration,
    ): Promise<void> {
        logger.info('[WEB CHAT] setupNewDevice: starting new device crypto setup', this.encCtx({ event: 'new_device_start' }));
        if (!setupClient) throw new Error('Client not initialized');
        this.assertSessionCurrent(generation, setupClient);

        let crossSigningCompleted = false;
        let crossSigningFailed = false;

        try {
            this.chatState.crossSigningStatus = Status.InProgress;
            this.chatState.backupStatus = Status.InProgress;
            this.emitStateChange();

            const crypto = setupClient.getCrypto();
            if (!crypto) throw new Error('Crypto not available');

            logger.info('[WEB CHAT] setupNewDevice: bootstrapping cross-signing', this.encCtx({ event: 'new_device_cross_signing_start' }));
            try {
                await crypto.bootstrapCrossSigning({
                    authUploadDeviceSigningKeys: async (makeRequest) => {
                        return makeRequest(null);
                    },
                });
                crossSigningCompleted = true;
                logger.info('[WEB CHAT] setupNewDevice: cross-signing bootstrapped', this.encCtx({ event: 'new_device_cross_signing_done' }));
            } catch (error) {
                crossSigningFailed = true;
                logger.error('[WEB CHAT] setupNewDevice: cross-signing recovery failed; continuing with key backup recovery', this.encCtx({
                    event: 'new_device_cross_signing_failed',
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined,
                }));
            }
            this.assertSessionCurrent(generation, setupClient);

            logger.info('[WEB CHAT] setupNewDevice: bootstrapping secret storage', this.encCtx({ event: 'new_device_secret_storage_start' }));
            await crypto.bootstrapSecretStorage({});
            logger.info('[WEB CHAT] setupNewDevice: secret storage bootstrapped', this.encCtx({ event: 'new_device_secret_storage_done' }));
            this.assertSessionCurrent(generation, setupClient);

            const backupInfo = await crypto.getKeyBackupInfo();
            logger.info('[WEB CHAT] setupNewDevice: key backup check', this.encCtx({
                event: 'new_device_backup_check',
                hasBackup: !!backupInfo,
                backupVersion: backupInfo?.version,
            }));
            this.assertSessionCurrent(generation, setupClient);

            if (!backupInfo) {
                throw new Error('No backup found on server');
            }

            const trustBeforeKeyLoad = await crypto.isKeyBackupTrusted(backupInfo);
            this.assertSessionCurrent(generation, setupClient);
            this.logUntrustedKeyBackup(String(backupInfo.version), trustBeforeKeyLoad, {
                reason: 'new_device_before_key_load',
            });

            logger.info('[WEB CHAT] setupNewDevice: loading backup key from Secret Storage', this.encCtx({
                event: 'new_device_backup_key_load_start',
                backupVersion: backupInfo.version,
            }));
            await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
            this.assertSessionCurrent(generation, setupClient);

            const trustInfo = await crypto.isKeyBackupTrusted(backupInfo);
            this.assertSessionCurrent(generation, setupClient);
            if (!trustInfo.matchesDecryptionKey) {
                logger.error('[WEB CHAT] setupNewDevice: backup key does not match server backup', this.encCtx({
                    event: 'new_device_backup_key_mismatch',
                    backupVersion: backupInfo.version,
                    trusted: trustInfo.trusted,
                    matchesDecryptionKey: trustInfo.matchesDecryptionKey,
                }));
                throw new Error('Backup key does not match server backup!');
            }

            const enabledBackup = await this.checkKeyBackupAndEnableWithLogging(crypto, {
                reason: 'new_device_after_key_load',
            });
            this.assertSessionCurrent(generation, setupClient);
            const activeBackupVersion = await crypto.getActiveSessionBackupVersion();
            this.assertSessionCurrent(generation, setupClient);
            if (!enabledBackup?.backupInfo || activeBackupVersion !== String(backupInfo.version)) {
                throw new Error('Backup not enabled after loading its decryption key');
            }

            logger.info('[WEB CHAT] setupNewDevice: restoring key backup', this.encCtx({
                event: 'new_device_backup_restore_start',
                backupVersion: backupInfo.version,
            }));
            const restoreResult = await crypto.restoreKeyBackup();
            this.assertSessionCurrent(generation, setupClient);

            logger.info('[WEB CHAT] setupNewDevice: key backup restore completed', this.encCtx({
                event: 'new_device_backup_restore_complete',
                backupVersion: backupInfo.version,
                total: restoreResult.total,
                imported: restoreResult.imported,
            }));

            logger.info('[WEB CHAT] setupNewDevice: completed successfully', this.encCtx({
                event: 'new_device_complete',
                backupVersion: backupInfo.version,
                crossSigningRecovered: !crossSigningFailed,
            }));

            this.chatState.crossSigningStatus = crossSigningFailed ? Status.Error : Status.Complete;
            this.chatState.backupStatus = Status.Complete;
            this.chatState.hasSecretStorage = true;
            this.emitStateChange();

        } catch (error) {
            if (!this.isSessionCurrent(generation, setupClient)) throw error;
            logger.error(`[WEB CHAT] setupNewDevice failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'new_device_error',
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
            }));
            this.chatState.crossSigningStatus = crossSigningCompleted ? Status.Complete : Status.Error;
            this.chatState.backupStatus = Status.Error;
            this.emitStateChange();
            throw error;
        }
    }

    async downloadDeviceKeysForRoom(roomId: string): Promise<void> {
        if (this.downloadedKeys.has(roomId)) {
            return;
        }

        // Await the in-flight download instead of returning early — callers rely
        // on the keys actually being present when this resolves.
        const inFlight = this.downloadingKeys.get(roomId);
        if (inFlight) {
            return inFlight;
        }

        if (!this.client) throw new Error('Client not initialized');

        const crypto = this.client.getCrypto();
        if (!crypto) throw new Error('Crypto not available');

        const room = this.client.getRoom(roomId);
        if (!room) throw new Error('Room not found');

        if (!room.hasEncryptionStateEvent()) {
            return;
        }

        const client = this.client;
        const download = (async () => {
            try {
                const members = room.getJoinedMembers();
                const myUserId = client.getUserId()!;
                const userIds = members
                    .map(m => m.userId)
                    .filter(uid => uid !== myUserId);

                if (userIds.length > 0) {
                    await crypto.getUserDeviceInfo(userIds, true);
                }
                if (this.client === client) {
                    this.downloadedKeys.add(roomId);
                }
            } catch (error) {
                logger.error(`[WEB CHAT] Failed to download device keys: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({ event: 'download_device_keys_error', roomId, error: error instanceof Error ? error.message : String(error) }));
                throw error;
            } finally {
                if (this.client === client) {
                    this.downloadingKeys.delete(roomId);
                }
            }
        })();

        this.downloadingKeys.set(roomId, download);
        return download;
    }

    private setupCryptoEventListeners() {
        if (!this.client) return;
        const eventClient = this.client;
        const isCurrentClient = () => this.client === eventClient;

        eventClient.on('Event.decrypted' as any, (event: MatrixEvent) => {
            if (!isCurrentClient()) return;
            const roomId = event.getRoomId();
            if (roomId) {
                const room = eventClient.getRoom(roomId);
                if (room) {
                    if (event.getType() === MATRIX_CALL_RINGING_EVENT_TYPE) {
                        if (event.getSender() !== eventClient.getUserId()) this.emitCallRinging(event);
                        return;
                    }
                    if (event.getType() === MATRIX_CALL_ESCALATE_EVENT_TYPE) {
                        if (event.getSender() !== eventClient.getUserId()) this.emitCallEscalation(event);
                        return;
                    }
                    if (event.getType() === RTC_PROTOCOL_V1.notificationEventType) {
                        this.emit('rtcNotification', {event, room});
                        this.emit('messageUpdate', {event, room});
                        return;
                    }
                    if (event.getType() === RTC_PROTOCOL_V1.declineEventType) {
                        this.emit('messageUpdate', {event, room});
                        return;
                    }
                    if (isMessageEdit(event)) return;
                    this.emit('newMessage', {event, room});
                    // An encrypted room's call events arrive as ciphertext and
                    // only become `m.call.*` here, so this is the first point a
                    // call's row can be drawn or re-decided.
                    if (isCallTimelineEvent(event)) this.emit('callEvent', {event, room});
                }
            }
        });

        eventClient.on('Event.decryptionFailure' as any, (event: MatrixEvent, error: Error) => {
            if (!isCurrentClient()) return;
            logger.error(`[WEB CHAT] Decryption failed: ${error.message}`, this.encCtx({
                event: 'decryption_failure',
                roomId: event.getRoomId() || undefined,
                eventId: event.getId() || undefined,
                sender: event.getSender() || undefined,
                error: error.message,
            }));
        });

        eventClient.on(CryptoEvent.KeyBackupStatus, (enabled: boolean) => {
            if (!isCurrentClient()) return;
            logger.info(`[WEB CHAT] Key backup status changed: ${enabled ? 'enabled' : 'disabled'}`, this.encCtx({
                event: 'key_backup_status',
                enabled,
            }));
            this.chatState.backupStatus = enabled ? Status.Complete : Status.Error;
            this.emitStateChange();
        });

        eventClient.on(CryptoEvent.KeyBackupSessionsRemaining, (remaining: number) => {
            if (!isCurrentClient()) return;
            if (remaining > 0 && remaining % 50 === 0) {
                logger.info(`[WEB CHAT] Key backup sessions remaining: ${remaining}`, this.encCtx({
                    event: 'key_backup_progress',
                    remaining,
                }));
            }
        });

        eventClient.on(CryptoEvent.KeyBackupFailed, (errcode: string) => {
            if (!isCurrentClient()) return;
            logger.error(`[WEB CHAT] Key backup failed: ${errcode}`, this.encCtx({
                event: 'key_backup_failed',
                errcode,
            }));
            this.chatState.backupStatus = Status.Error;
            this.emitStateChange();
        });

        eventClient.on(CryptoEvent.DevicesUpdated, (users: string[]) => {
            if (!isCurrentClient()) return;
            logger.info(`[WEB CHAT] Devices updated for ${users.length} user(s)`, this.encCtx({
                event: 'devices_updated',
                userCount: users.length,
                users: users.slice(0, 5),
            }));
        });

        eventClient.on(CryptoEvent.VerificationRequestReceived, (request: VerificationRequest) => {
            if (!isCurrentClient()) return;
            this.emit('verificationRequest', request);
        });
    }

    private emitStateChange() {
        this.emit('stateChange', {
            ...this.chatState,
            rooms: [...this.chatState.rooms]
        });
    }

    private stopClientInstance(client: MatrixClient, logMessage: string, event: string): void {
        try {
            client.stopClient();
        } catch (error) {
            logger.warn(logMessage, this.encCtx({
                event,
                error: error instanceof Error ? error.message : String(error),
            }));
        }

        try {
            client.removeAllListeners();
        } catch (error) {
            logger.warn('[WEB CHAT] Failed to remove listeners from stopped Matrix client', this.encCtx({
                event: `${event}_remove_listeners_failed`,
                error: error instanceof Error ? error.message : String(error),
            }));
        }

        try {
            void client.store.destroy().catch((error) => {
                logger.warn('[WEB CHAT] Failed to close stopped Matrix store', this.encCtx({
                    event: `${event}_store_destroy_failed`,
                    error: error instanceof Error ? error.message : String(error),
                }));
            });
        } catch (error) {
            logger.warn('[WEB CHAT] Failed to close stopped Matrix store', this.encCtx({
                event: `${event}_store_destroy_failed`,
                error: error instanceof Error ? error.message : String(error),
            }));
        }

        if (this.client === client) {
            this.client = null;
        }
    }

    private stopActiveClient(logMessage: string, event: string): void {
        const activeClient = this.client;
        if (!activeClient) return;
        this.stopClientInstance(activeClient, logMessage, event);
    }

    private invalidateInitialization(): Promise<void> | null {
        this.sessionGeneration += 1;
        this.sessionAbortController?.abort();
        this.sessionAbortController = null;

        // Keep the stale initialization registered until it settles. Opening a
        // second MatrixClient while the first one still owns the same Rust
        // Crypto IndexedDB can corrupt the store or close its live connection.
        return this.initializationInFlight?.promise ?? null;
    }

    private clearInMemorySession(sessionLockState: MatrixSessionLockState): void {
        setAiBotSession(undefined);
        this.explicitRoomSelected = false;
        this.openedRoomIds.clear();
        if (this.previewPrefetchRefreshTimeout !== null) {
            window.clearTimeout(this.previewPrefetchRefreshTimeout);
            this.previewPrefetchRefreshTimeout = null;
        }
        if (this.roomsListUpdateTimeout !== null) {
            window.clearTimeout(this.roomsListUpdateTimeout);
            this.roomsListUpdateTimeout = null;
        }

        this.chatState = {
            isInitialized: false,
            isAuthenticated: false,
            hasCompletedInitialSync: false,
            initialRoomRestoreSettled: false,
            sessionLockState,
            currentUserId: null,
            selectedRoomId: null,
            rooms: [],
            syncState: 'STOPPED',
            error: null,
            isCryptoEnabled: false,
            isDeviceVerified: false,
            hasSecretStorage: false,
            crossSigningStatus: Status.NotStarted,
            backupStatus: Status.NotStarted,
            useSlidingSync: true,
            clientReady: false,
            cryptoReady: false,
            deviceKeysStatus: 'idle',
            deviceKeysProgress: { total: 0, loaded: 0, failed: 0 },
        };

        this.processingRooms.clear();
        this.existingRoomIds.clear();
        this.downloadingKeys.clear();
        this.downloadedKeys.clear();
        this.roomsInSetup.clear();
        this.roomsPendingEncryption.clear();
        this.roomPendingMemberIds.clear();
        this.roomInitialInviteeIds.clear();
        this.localRooms = [];
        this.localToRealRoomMap.clear();
        this.lastMessageOverrides.clear();
        this.pendingRoomDataCount = 0;
        this.deviceId = null;
    }

    setSessionLockState(sessionLockState: MatrixSessionLockState): void {
        if (this.chatState.sessionLockState === sessionLockState) return;
        this.chatState.sessionLockState = sessionLockState;
        this.emitStateChange();
    }

    requestSessionTakeover(): void {
        this.setSessionLockState('takeover_pending');
        this.emit('sessionTakeoverRequested', null);
    }

    async stopForSessionLockLoss(): Promise<void> {
        const staleInitialization = this.invalidateInitialization();
        this.cachedSecretStorageKey = null;
        this.matrixRefreshToken = null;
        this.clearTokenRefreshTimeout();
        this.tokenRefreshInFlight = null;
        this.tokenExpiryRecoveryInFlight = null;
        this.stopActiveClient(
            '[WEB CHAT] Failed to stop Matrix client after session lock loss',
            'matrix_lock_loss_stop_failed'
        );
        slidingSyncManager.stop();
        this.clearInMemorySession('standby');
        this.emitStateChange();

        // The superseded initialization owns its captured client and stops it
        // in its catch path. Keep the cross-tab lock until that cleanup has
        // completed so the claimant cannot open the same Rust Crypto database.
        try {
            await staleInitialization;
        } catch {
            // Superseded initialization is expected to reject.
        }
    }

    private emit(event: string, data: any) {
        const callbacks = this.eventCallbacks.get(event) || [];
        callbacks.forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                logger.error(`[WEB CHAT] Error in event callback: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'event_callback_error', error: error instanceof Error ? error.message : String(error) });
            }
        });
    }

    on(event: string, callback: Function) {
        if (!this.eventCallbacks.has(event)) {
            this.eventCallbacks.set(event, []);
        }
        this.eventCallbacks.get(event)!.push(callback);
    }

    off(event: string, callback: Function) {
        const callbacks = this.eventCallbacks.get(event) || [];
        const index = callbacks.indexOf(callback);
        if (index > -1) {
            callbacks.splice(index, 1);
        }
    }

    private clearTokenRefreshTimeout(): void {
        if (this.tokenRefreshTimeoutId !== null) {
            window.clearTimeout(this.tokenRefreshTimeoutId);
            this.tokenRefreshTimeoutId = null;
        }
        this.tokenRefreshDueAtMs = null;
        this.tokenRefreshFailures = 0;
        this.matrixTokenExpiresAtMs = null;
        this.rejectedAccessToken = null;
        this.detachTokenRefreshWakeListeners?.();
        this.detachTokenRefreshWakeListeners = null;
    }

    private currentTokenSession(): MatrixTokenSession | null {
        const client = this.client;
        const signal = this.sessionAbortController?.signal;
        if (!client || !signal) return null;
        return {client, generation: this.sessionGeneration, signal};
    }

    private isTokenSessionCurrent(session: MatrixTokenSession): boolean {
        return this.isSessionCurrent(session.generation, session.client);
    }

    private scheduleTokenRefresh(session: MatrixTokenSession, expiresInMs: number | null): void {
        this.clearTokenRefreshTimeout();
        if (typeof expiresInMs !== 'number' || !this.matrixRefreshToken) return;

        this.matrixTokenExpiresAtMs = Date.now() + Math.max(0, expiresInMs);
        this.tokenRefreshDueAtMs = Date.now() + Math.max(0, expiresInMs) * MATRIX_TOKEN_REFRESH_LIFETIME_RATIO;
        this.armTokenRefreshTimer(session);
        this.attachTokenRefreshWakeListeners(session);
    }

    private armTokenRefreshTimer(session: MatrixTokenSession): void {
        if (this.tokenRefreshTimeoutId !== null) {
            window.clearTimeout(this.tokenRefreshTimeoutId);
            this.tokenRefreshTimeoutId = null;
        }
        if (this.tokenRefreshDueAtMs === null) return;

        const delayMs = Math.min(
            Math.max(0, this.tokenRefreshDueAtMs - Date.now()),
            MATRIX_TOKEN_REFRESH_CHECK_INTERVAL_MS,
        );
        this.tokenRefreshTimeoutId = window.setTimeout(() => {
            this.tokenRefreshTimeoutId = null;
            if (!this.isTokenSessionCurrent(session)) return;
            if (this.isTokenRefreshDue()) {
                this.startDueTokenRefresh(session, 'timer');
                return;
            }
            this.armTokenRefreshTimer(session);
        }, delayMs);
    }

    private attachTokenRefreshWakeListeners(session: MatrixTokenSession): void {
        if (typeof document === 'undefined') return;

        const refreshIfDue = (trigger: string) => {
            if (!this.isTokenSessionCurrent(session) || !this.isTokenRefreshDue()) return;
            this.startDueTokenRefresh(session, trigger);
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshIfDue('visibilitychange');
            }
        };
        const onPageShow = () => refreshIfDue('pageshow');
        const onOnline = () => refreshIfDue('online');

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pageshow', onPageShow);
        window.addEventListener('online', onOnline);
        this.detachTokenRefreshWakeListeners = () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pageshow', onPageShow);
            window.removeEventListener('online', onOnline);
        };
    }

    private isTokenRefreshDue(): boolean {
        return this.tokenRefreshDueAtMs !== null && Date.now() >= this.tokenRefreshDueAtMs;
    }

    private isAccessTokenExpired(): boolean {
        return this.matrixTokenExpiresAtMs !== null && Date.now() >= this.matrixTokenExpiresAtMs;
    }

    private async tryRefreshAccessToken(session: MatrixTokenSession): Promise<string | null> {
        try {
            return (await this.refreshTokens(session)).accessToken;
        } catch {
            return null;
        }
    }

    private async fetchWithFreshAccessToken(
        session: MatrixTokenSession,
        input: RequestInfo | URL,
        init?: RequestInit,
    ): Promise<Response> {
        const sentToken = bearerTokenOf(init);
        if (!sentToken || !this.isTokenSessionCurrent(session) || !isTokenRefreshableRequest(session.client, input)) {
            return fetch(input, init);
        }

        const refreshAttempted = this.isAccessTokenExpired();
        const accessToken = refreshAttempted
            ? await this.tryRefreshAccessToken(session) ?? sentToken
            : sentToken;
        const response = await fetch(input, accessToken === sentToken ? init : withBearerToken(init, accessToken));
        if (!await isUnknownTokenResponse(response)) return response;

        const currentToken = session.client.getAccessToken();
        let retryToken = currentToken !== accessToken ? currentToken : null;
        if (!retryToken && !refreshAttempted && accessToken !== this.rejectedAccessToken) {
            retryToken = await this.tryRefreshAccessToken(session);
        }
        if (!retryToken || retryToken === accessToken || !this.isTokenSessionCurrent(session)) return response;

        const retried = await fetch(input, withBearerToken(init, retryToken));
        if (await isUnknownTokenResponse(retried)) {
            this.rejectedAccessToken = retryToken;
        }
        return retried;
    }

    private startDueTokenRefresh(session: MatrixTokenSession, trigger: string): void {
        if (this.tokenRefreshInFlight) return;
        this.refreshTokens(session).catch((error) => {
            if (error instanceof MatrixSessionSupersededError) return;
            logger.warn('[WEB CHAT] Scheduled Matrix token refresh failed', {
                source: 'chat',
                event: 'matrix_token_refresh_failed',
                trigger,
                attempt: this.tokenRefreshFailures,
                error: error instanceof Error ? error.message : String(error),
            });
        });
    }

    private scheduleTokenRefreshRetry(session: MatrixTokenSession): void {
        if (!this.isTokenSessionCurrent(session) || this.tokenRefreshDueAtMs === null) return;

        this.tokenRefreshFailures += 1;
        const retryDelayMs = Math.min(
            MATRIX_TOKEN_REFRESH_RETRY_BASE_MS * 2 ** (this.tokenRefreshFailures - 1),
            MATRIX_TOKEN_REFRESH_RETRY_MAX_MS,
        );
        this.tokenRefreshDueAtMs = Date.now() + retryDelayMs;
        this.armTokenRefreshTimer(session);
    }

    private async refreshTokens(session: MatrixTokenSession): Promise<MatrixTokenRefreshResult> {
        const {client, generation, signal} = session;
        this.assertSessionCurrent(generation, client);
        if (this.tokenRefreshInFlight) {
            return this.tokenRefreshInFlight;
        }

        const currentRefreshToken = this.matrixRefreshToken;
        if (!currentRefreshToken) {
            throw new Error('No refresh token available for Matrix session');
        }

        const refresh = this.performRefreshTokenRequest(currentRefreshToken, generation, signal)
            .then(
                (refreshed) => {
                    this.assertSessionCurrent(generation, client);
                    client.setAccessToken(refreshed.accessToken);
                    this.scheduleTokenRefresh(session, refreshed.expiresInMs);
                    return refreshed;
                },
                (error: unknown) => {
                    this.scheduleTokenRefreshRetry(session);
                    throw error;
                },
            )
            .finally(() => {
                if (this.tokenRefreshInFlight === refresh) {
                    this.tokenRefreshInFlight = null;
                }
            });
        this.tokenRefreshInFlight = refresh;

        return refresh;
    }

    private async performRefreshTokenRequest(
        refreshToken: string,
        generation: number,
        signal: AbortSignal,
    ): Promise<MatrixTokenRefreshResult> {
        const baseUrl = window.__ENV__?.SYNAPSE_BASE_URL;
        if (!baseUrl) {
            throw new Error('SYNAPSE_BASE_URL is not configured');
        }
        const response = await fetch(`${baseUrl}/_matrix/client/v3/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                refresh_token: refreshToken,
            }),
            signal,
        });

        let responseData: MatrixRefreshTokenResponse | Record<string, any> = {};
        try {
            responseData = await response.json();
        } catch {
            responseData = {};
        }
        this.assertSessionCurrent(generation);

        if (!response.ok) {
            if (response.status === 401) {
                logger.warn('[WEB CHAT] Matrix refresh token rejected; falling back to backend login', {
                    source: 'chat',
                    event: 'matrix_refresh_unauthorized_backend_login',
                });
                return this.loginForFreshMatrixTokens();
            }

            const error = new Error(`Failed to refresh Matrix token: ${response.status}`) as Error & {
                httpStatus?: number;
                data?: unknown;
            };
            error.httpStatus = response.status;
            error.data = responseData;
            throw error;
        }

        const nextAccessTokenRaw = (responseData as MatrixRefreshTokenResponse).access_token;
        if (!nextAccessTokenRaw) {
            throw new Error('Matrix refresh response does not include access_token');
        }

        const nextAccessToken = nextAccessTokenRaw;
        const nextRefreshToken = (responseData as MatrixRefreshTokenResponse).refresh_token || refreshToken;
        const expiresInMsRaw = (responseData as MatrixRefreshTokenResponse).expires_in_ms;
        const expiresInMs = typeof expiresInMsRaw === 'number' ? expiresInMsRaw : null;
        const expiresAtMs = typeof expiresInMs === 'number' ? Date.now() + expiresInMs : null;

        this.matrixRefreshToken = nextRefreshToken;

        store.dispatch(setMatrixSession({
            accessToken: nextAccessToken,
            refreshToken: nextRefreshToken,
            expiresAtMs,
        }));

        return {
            accessToken: nextAccessToken,
            refreshToken: nextRefreshToken,
            expiresInMs,
        };
    }

    private async loginForFreshMatrixTokens(): Promise<MatrixTokenRefreshResult> {
        const state = store.getState();
        const deviceId = this.deviceId ?? state.auth?.deviceId ?? null;
        const loginRequest = store.dispatch(chatApi.endpoints.login.initiate({deviceId}));
        let authResponse: ChatLoginResponse;

        try {
            authResponse = await loginRequest.unwrap();
        } finally {
            loginRequest.reset();
        }

        const accessToken = authResponse.accessToken.startsWith('Bearer ')
            ? authResponse.accessToken.substring(7)
            : authResponse.accessToken;
        const nextRefreshToken = authResponse.refreshToken?.startsWith('Bearer ')
            ? authResponse.refreshToken.substring(7)
            : authResponse.refreshToken;
        const expiresInMs = authResponse.expiresInMs ?? null;

        this.matrixRefreshToken = nextRefreshToken ?? null;
        store.dispatch(setMatrixSession({
            accessToken,
            refreshToken: nextRefreshToken ?? null,
            expiresAtMs: typeof expiresInMs === 'number'
                ? Date.now() + expiresInMs
                : null,
        }));

        logger.info('[WEB CHAT] Matrix session recovered through backend login', {
            source: 'chat',
            event: 'matrix_backend_login_recovery_success',
            hasRefreshToken: Boolean(nextRefreshToken),
            expiresInMs,
        });

        return {
            accessToken,
            refreshToken: nextRefreshToken,
            expiresInMs,
        };
    }

    getLocalRooms(): LocalRoom[] {
        return [...this.localRooms];
    }

    getLastMessageOverride(roomId: string): LastMessageOverride | null {
        return this.lastMessageOverrides.get(roomId) || null;
    }

    clearLastMessageOverride(roomId: string): void {
        if (this.lastMessageOverrides.delete(roomId)) {
            this.emit('lastMessageOverride', { roomId });
        }
    }

    createLocalRoom(data: { name?: string; otherUsers: LocalRoomUser[]; isDirect: boolean }): string {
        const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        this.localRooms.push({
            localId,
            name: data.name,
            otherUsers: data.otherUsers,
            createdAt: Date.now(),
            isDirect: data.isDirect,
        });
        this.emit('localRoomsChange', { localId });
        return localId;
    }

    mapLocalToRealRoom(localId: string, realRoomId: string): void {
        this.localToRealRoomMap.set(localId, realRoomId);
        this.emit('localRoomsChange', { localId });
    }

    getRealRoomIdsForLocalRooms(): Set<string> {
        return new Set(this.localToRealRoomMap.values());
    }

    getMappedRealRoomId(localId: string): string | null {
        return this.localToRealRoomMap.get(localId) || null;
    }

    private isResolvedRoomName(name?: string): boolean {
        if (!name) return false;
        return !name.includes('chat.chte.ch') && name !== 'Empty room';
    }

    private isRealRoomReadyForLocalPromotion(room: Room): boolean {
        if (room.getMyMembership() !== 'join') return false;
        if (this.isResolvedRoomName(room.name)) return true;

        const currentUserId = this.getCurrentUserMatrixId();
        return room.getJoinedMembers().some(member =>
            member.userId !== currentUserId && !isServiceParticipant(member.userId)
        );
    }

    reconcileLocalRoomsWithRealRooms(): void {
        if (!this.client || this.localToRealRoomMap.size === 0) return;

        const mappings = Array.from(this.localToRealRoomMap.entries());
        mappings.forEach(([localId, realRoomId]) => {
            const realRoom = this.client?.getRoom(realRoomId);
            if (!realRoom) return;

            if (this.isRealRoomReadyForLocalPromotion(realRoom)) {
                this.removeLocalRoom(localId);
            }
        });
    }

    removeLocalRoom(localId: string): void {
        const before = this.localRooms.length;
        this.localRooms = this.localRooms.filter(room => room.localId !== localId);
        this.localToRealRoomMap.delete(localId);
        if (this.localRooms.length !== before) {
            this.emit('localRoomsChange', { localId });
        }
    }

    private findParentRoom(): Room | null {
        if (!this.client) return null;

        const rooms = this.client.getRooms();
        return rooms.find(room => room.isSpaceRoom()) || null;
    }

    async initialize(
        authResponse: ChatLoginResponse,
    ): Promise<void> {
        this.lastInitDevice = null;
        const requestedAuthContext = store.getState().auth;
        if (!requestedAuthContext.tenant || !requestedAuthContext.userId) {
            throw new Error('Application account context is missing');
        }

        let initializationDrained = false;
        while (!initializationDrained) {
            if (this.logoutInFlight) {
                await this.logoutInFlight;
            }

            const currentAuthContext = store.getState().auth;
            if (
                currentAuthContext.tenant !== requestedAuthContext.tenant ||
                currentAuthContext.userId !== requestedAuthContext.userId
            ) {
                throw new MatrixSessionSupersededError();
            }

            const activeInitialization = this.initializationInFlight;
            if (!activeInitialization) {
                initializationDrained = true;
                continue;
            }

            if (activeInitialization.generation === this.sessionGeneration) {
                return activeInitialization.promise;
            }

            // An invalidated initialization may still be inside an SDK or
            // IndexedDB operation which AbortSignal cannot cancel. Drain it
            // before opening another client on the same crypto database.
            try {
                await activeInitialization.promise;
            } catch {
                // A superseded initialization is expected to reject. Its own
                // catch path is responsible for stopping its captured client.
            }
        }

        this.sessionAbortController?.abort();
        const generation = ++this.sessionGeneration;
        const abortController = new AbortController();
        this.sessionAbortController = abortController;
        const initialization = this.performInitialize(authResponse, generation, abortController.signal);
        const initializationEntry = {
            generation,
            promise: initialization,
        };
        this.initializationInFlight = initializationEntry;

        try {
            await initialization;
        } finally {
            if (this.initializationInFlight === initializationEntry) {
                this.initializationInFlight = null;
            }
        }
    }

    /** Last stage performInitialize entered; read after a failure to say where it died. */
    getInitStage(): string {
        return this.initStage;
    }

    /** Device the latest init attempt logged in as (null if it died before /whoami). Lets a retry reuse it instead of minting another. */
    getLastInitDeviceId(tenant: string, userId: string): string | null {
        const last = this.lastInitDevice;
        return last && last.tenant === tenant && last.userId === userId ? last.deviceId : null;
    }

    private async performInitialize(
        authResponse: ChatLoginResponse,
        generation: number,
        signal: AbortSignal,
    ): Promise<void> {
        let initializationClient: MatrixClient | null = null;
        this.initStage = 'client_setup';
        this.lastInitDevice = null;
        try {
            this.assertSessionCurrent(generation);
            const {accessToken, refreshToken} = authResponse;

            if (!accessToken) {
                throw new Error('No access token received');
            }

            const authStateAtStart = store.getState().auth;
            const tenant = authStateAtStart.tenant;
            const authUserId = authStateAtStart.userId;
            if (!tenant || !authUserId) {
                throw new Error('Application account context is missing');
            }

            const assertAuthContextUnchanged = () => {
                this.assertSessionCurrent(generation, initializationClient ?? undefined);
                const currentAuth = store.getState().auth;
                if (currentAuth.tenant !== tenant || currentAuth.userId !== authUserId) {
                    throw new Error('Application account changed during Matrix initialization');
                }
            };

            if (this.client) {
                this.stopActiveClient(
                    '[WEB CHAT] Failed to stop previous Matrix client before initialization',
                    'matrix_reinitialize_stop_failed'
                );
                slidingSyncManager.stop();
                this.clearInMemorySession('active');
                this.emitStateChange();
            }

            const {user_id: userId, device_id: newDeviceId} = await this.extractUserIdFromToken(accessToken, signal);
            assertAuthContextUnchanged();

            const storedApplicationDeviceId = getStoredDeviceId(tenant, authUserId);
            setAiBotSession(userId);
            const storedMatrixDeviceId = getStoredDeviceId(tenant, userId);
            this.deviceId = newDeviceId;
            this.lastInitDevice = typeof newDeviceId === 'string' ? {tenant, userId: authUserId, deviceId: newDeviceId} : null;

            if (typeof this.deviceId !== "string") {
                throw new Error('Invalid device ID format');
            }

            const storeId = userId.replace(/[^a-zA-Z0-9]/g, '_');
            const storeBaseName = `matrix-store-${storeId}`;
            const cryptoStoreName = `matrix-crypto-${storeId}`;
            const cryptoDbPrefix = `matrix-sdk-crypto-${storeId}`;

            const recoveryDeviceId = getMatrixDeviceIdRecoveryCandidate(
                storedMatrixDeviceId,
                this.deviceId,
            );
            if (recoveryDeviceId) {
                logger.warn('[WEB CHAT] Matrix device ID differs from preserved encryption store; retrying the stored device before opening crypto', this.encCtx({
                    event: 'matrix_device_id_recovery_required',
                    applicationUserId: authUserId,
                    matrixUserId: userId,
                    receivedDeviceId: this.deviceId,
                    storedDeviceId: recoveryDeviceId,
                }));
                throw new MatrixDeviceIdMismatchError(recoveryDeviceId, this.deviceId);
            }

            const matrixStore = new IndexedDBStore({
                indexedDB: window.indexedDB,
                dbName: storeBaseName,
            });

            const cryptoStore = new IndexedDBCryptoStore(
                window.indexedDB,
                cryptoStoreName
            );

            const cleanAccessToken = accessToken.startsWith('Bearer ')
                ? accessToken.substring(7)
                : accessToken;

            const cleanRefreshToken = refreshToken?.startsWith('Bearer ')
                ? refreshToken.substring(7)
                : refreshToken;

            this.matrixRefreshToken = cleanRefreshToken ?? null;

            const clientOpts: ICreateClientOpts = {
                baseUrl: window.__ENV__?.SYNAPSE_BASE_URL,
                userId,
                accessToken: cleanAccessToken,
                fetchFn: (input, init) => initializationClient
                    ? this.fetchWithFreshAccessToken({client: initializationClient, generation, signal}, input, init)
                    : fetch(input, init),
                deviceId: this.deviceId,
                store: matrixStore,
                cryptoStore,
                timelineSupport: true,
                useAuthorizationHeader: true,
                cryptoCallbacks: this.createCryptoCallbacks(generation, signal),
                roomNameGenerator: (roomId, state) => {
                    if (state.type === RoomNameType.Actual) return null;
                    // The room is not in the store yet on the initial sliding-sync pass
                    // (recalculate() runs before storeRoom()), so getRoom() misses here and
                    // buildRoomName falls back to the server's hero names.
                    const room = initializationClient?.getRoom(roomId);
                    const humanMembers = room?.getJoinedMembers()
                        .filter(member => member.userId !== userId && !isServiceParticipant(member.userId))
                        .map(member => member.rawDisplayName || member.name || member.userId);
                    // Alone in the room, e.g. a DM the other person left. Answered for both the
                    // SDK's Generated and EmptyRoom passes, so it never wraps our own answer
                    // into "Empty room (was Empty room)". A leave event carries no displayname,
                    // so the name comes from the membership it replaced.
                    if (room && !humanMembers?.length) {
                        const formerMemberNames = room.getMembers()
                            .filter(member => member.userId !== userId && !isServiceParticipant(member.userId))
                            .filter(member => member.membership === 'leave' || member.membership === 'ban')
                            .map(member => member.events.member?.getPrevContent()?.displayname || member.rawDisplayName);
                        return buildEmptyRoomName(formerMemberNames);
                    }
                    return state.type === RoomNameType.Generated ? buildRoomName(state, humanMembers) : null;
                },
            };

            initializationClient = createClient(clientOpts);
            releaseRejectedMediaRequests(initializationClient.getMediaHandler());
            this.assertSessionCurrent(generation);
            this.client = initializationClient;
            this.tokenRefreshInFlight = null;
            this.tokenExpiryRecoveryInFlight = null;
            this.scheduleTokenRefresh({client: initializationClient, generation, signal}, authResponse.expiresInMs ?? null);

            this.initStage = 'store_startup';
            await matrixStore.startup();
            assertAuthContextUnchanged();
            await cryptoStore.startup();
            assertAuthContextUnchanged();
            this.setupEventListeners();
            this.setupCryptoEventListeners();

            const cryptoWasInitialised = localStorage.getItem(`mx_crypto_initialised:${storeId}`) === 'true';
            const hadPreviousDevice = Boolean(storedApplicationDeviceId || storedMatrixDeviceId);

            if (cryptoWasInitialised || hadPreviousDevice) {
                const rustDbExists = await this.checkRustCryptoDbExists(cryptoDbPrefix);
                assertAuthContextUnchanged();
                if (!rustDbExists) {
                    logger.error('[WEB CHAT] Rust crypto DB missing but device was previously active — storage evicted', this.encCtx({
                        event: 'crypto_db_evicted',
                        storeId,
                        cryptoWasInitialised,
                        hadPreviousDevice,
                    }));
                    localStorage.removeItem(`mx_crypto_initialised:${storeId}`);
                    removeDeviceId(tenant, authUserId);
                    removeDeviceId(tenant, userId);
                    await initializationClient.clearStores({ cryptoDatabasePrefix: cryptoDbPrefix });
                    assertAuthContextUnchanged();
                    await this.deleteIndexedDBByPrefix(cryptoDbPrefix);
                    assertAuthContextUnchanged();
                    throw new MatrixCryptoStoreEvictedError();
                }
            }

            this.initStage = 'crypto_init';
            await initializationClient.initRustCrypto({
                cryptoDatabasePrefix: cryptoDbPrefix,
            });
            assertAuthContextUnchanged();
            // Keep both scopes aligned during the migration period. App startup
            // knows the application user ID, while the crypto store is keyed by
            // the Matrix user ID learned from /whoami.
            persistDeviceId(newDeviceId, tenant, authUserId);
            persistDeviceId(newDeviceId, tenant, userId);
            setChatStorageItem(`mx_crypto_initialised:${storeId}`, 'true');
            this.chatState.isCryptoEnabled = true;
            this.chatState.cryptoReady = true;

            const crypto = initializationClient.getCrypto();
            if (crypto) {
                crypto.setTrustCrossSignedDevices(true);
                crypto.globalBlacklistUnverifiedDevices = false;
            }

            const slidingSync = slidingSyncManager.setup(initializationClient);

            this.initStage = 'start_client';
            await initializationClient.startClient({
                slidingSync,
            });
            assertAuthContextUnchanged();

            logger.info('[WEB CHAT] Waiting for initial sync before encryption recovery', this.encCtx({
                event: 'encryption_initial_sync_wait',
            }));
            this.initStage = 'initial_sync';
            await this.waitForInitialSyncPrepared(initializationClient, 30_000, signal);
            assertAuthContextUnchanged();
            logger.info('[WEB CHAT] Initial sync prepared; starting encryption recovery', this.encCtx({
                event: 'encryption_initial_sync_prepared',
            }));

            // Only a store from an earlier session can believe its keys are on the
            // server when they are not; a fresh store uploads them on its own.
            if (cryptoWasInitialised) {
                this.initStage = 'own_device_keys_check';
                const checkedClient = initializationClient;
                const checkedDeviceId = this.deviceId;
                const deviceKeysStatus = await checkOwnDeviceKeys(userId, checkedDeviceId, {
                    queryOwnKeys: () => checkedClient.downloadKeysForUsers([userId]),
                    ownEd25519: async () => {
                        const crypto = checkedClient.getCrypto();
                        if (!crypto) throw new Error('Crypto is not initialised');
                        return (await crypto.getOwnDeviceKeys()).ed25519;
                    },
                    wait: ms => new Promise(resolve => window.setTimeout(resolve, ms)),
                });
                assertAuthContextUnchanged();
                if (deviceKeysStatus === 'missing' || deviceKeysStatus === 'mismatch') {
                    logger.error('[WEB CHAT] Own device keys are not published on the server; replacing the device', this.encCtx({
                        event: 'own_device_keys_missing',
                        status: deviceKeysStatus,
                        deviceId: checkedDeviceId,
                    }));
                    throw new MatrixOwnDeviceKeysMissingError(deviceKeysStatus, {
                        storeId,
                        cryptoDbPrefix,
                        tenant,
                        authUserId,
                        matrixUserId: userId,
                    });
                }
            }

            this.chatState.clientReady = true;
            this.chatState.isAuthenticated = true;
            this.chatState.sessionLockState = 'active';
            this.chatState.currentUserId = userId;
            this.chatState.syncState = 'SYNCING';
            this.chatState.error = null;
            this.emitStateChange();

            // Block until encryption setup completes — UI shows spinner via !isInitialized
            assertAuthContextUnchanged();
            this.initStage = 'encryption_setup';
            await this.setupEncryption(initializationClient, generation, signal);
            assertAuthContextUnchanged();

            this.initStage = 'ready';
            this.chatState.isInitialized = true;
            this.emitStateChange();


        } catch (error) {
            const isCurrentInitialization = this.isSessionCurrent(
                generation,
                initializationClient ?? undefined,
            );
            if (initializationClient) {
                this.stopClientInstance(
                    initializationClient,
                    '[WEB CHAT] Failed to stop Matrix client after initialization failure',
                    'matrix_initialization_failure_stop_failed',
                );
            }

            if (!isCurrentInitialization) {
                throw error;
            }

            if (error instanceof MatrixOwnDeviceKeysMissingError && initializationClient) {
                await this.discardUnpublishedDevice(initializationClient, error.stores);
            }

            this.matrixRefreshToken = null;
            this.clearTokenRefreshTimeout();
            this.tokenRefreshInFlight = null;
            this.tokenExpiryRecoveryInFlight = null;
            slidingSyncManager.stop();
            this.clearInMemorySession('active');
            if (this.isAuthenticationError(error)) {
                this.chatState.error = 'Authentication failed. Please check your credentials.';
            } else {
                this.chatState.error = error instanceof Error ? error.message : 'Unknown error';
            }

            this.chatState.isInitialized = false;
            this.chatState.isAuthenticated = false;
            this.chatState.isCryptoEnabled = false;
            this.chatState.clientReady = false;
            this.chatState.cryptoReady = false;
            this.emitStateChange();
            throw error;
        }
    }

    private async isCryptoAlreadySetUp(setupClient: MatrixClient, generation: number): Promise<boolean> {
        this.assertSessionCurrent(generation, setupClient);
        const crypto = setupClient.getCrypto();
        if (!crypto) return false;

        try {
            const [crossSigningReady, activeBackup] = await Promise.all([
                crypto.isCrossSigningReady(),
                crypto.getActiveSessionBackupVersion(),
            ]);
            this.assertSessionCurrent(generation, setupClient);

            logger.info('[WEB CHAT] isCryptoAlreadySetUp: check result', this.encCtx({
                event: 'crypto_setup_check',
                crossSigningReady,
                activeBackupVersion: activeBackup,
            }));

            return !!(crossSigningReady && activeBackup);
        } catch (error) {
            if (!this.isSessionCurrent(generation, setupClient)) throw error;
            logger.warn(`[WEB CHAT] isCryptoAlreadySetUp: check failed, proceeding with setup: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'crypto_setup_check_error',
                error: error instanceof Error ? error.message : String(error),
            }));
            return false;
        }
    }

    private async setupEncryption(
        setupClient: MatrixClient,
        generation: number,
        signal: AbortSignal,
    ): Promise<void> {
        try {
            logger.info('[WEB CHAT] setupEncryption: starting', this.encCtx({ event: 'encryption_setup_start' }));
            this.assertSessionCurrent(generation, setupClient);

            if (await this.isCryptoAlreadySetUp(setupClient, generation)) {
                logger.info('[WEB CHAT] setupEncryption: crypto already configured, enabling key backup engine', this.encCtx({ event: 'encryption_setup_skip' }));
                const crypto = setupClient.getCrypto();
                if (crypto) {
                    try {
                        await this.checkKeyBackupAndEnableWithLogging(crypto, {
                            reason: 'existing_crypto_setup',
                        });
                        this.assertSessionCurrent(generation, setupClient);
                        logger.info('[WEB CHAT] setupEncryption: key backup engine enabled', this.encCtx({ event: 'encryption_setup_backup_enabled' }));
                    } catch (err) {
                        if (!this.isSessionCurrent(generation, setupClient)) throw err;
                        logger.warn(`[WEB CHAT] setupEncryption: failed to enable key backup engine: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({
                            event: 'encryption_setup_backup_enable_error',
                            error: err instanceof Error ? err.message : String(err),
                        }));
                    }
                }
                this.chatState.crossSigningStatus = Status.Complete;
                this.chatState.backupStatus = Status.Complete;
                this.chatState.hasSecretStorage = true;
                this.chatState.deviceKeysStatus = 'ready';
                this.emitStateChange();
                return;
            }

            this.assertSessionCurrent(generation, setupClient);
            const isFirstDevice = await this.isFirstDeviceSetup(setupClient, generation);
            this.assertSessionCurrent(generation, setupClient);

            logger.info(`[WEB CHAT] setupEncryption: detected ${isFirstDevice ? 'first' : 'new'} device`, this.encCtx({
                event: 'encryption_setup_device_type',
                isFirstDevice,
            }));

            if (isFirstDevice) {
                await this.setupFirstDevice(setupClient, generation, signal);
            } else {
                await this.setupNewDevice(setupClient, generation);
            }

            this.assertSessionCurrent(generation, setupClient);

            logger.info('[WEB CHAT] setupEncryption: completed', this.encCtx({ event: 'encryption_setup_complete' }));

            this.chatState.deviceKeysStatus = 'ready';
            this.emitStateChange();

        } catch (error) {
            if (!this.isSessionCurrent(generation, setupClient)) {
                logger.info('[WEB CHAT] setupEncryption: aborted due to logout', this.encCtx({ event: 'encryption_setup_aborted' }));
                throw error;
            }
            logger.error(`[WEB CHAT] setupEncryption failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'encryption_setup_error',
                error: error instanceof Error ? error.message : String(error),
            }));
            this.chatState.crossSigningStatus = Status.Error;
            this.chatState.backupStatus = Status.Error;
            this.emitStateChange();
            this.emit('cryptoSetupFailed', error);
        }
    }

    getLastReadEventId(roomId: string): string | null {
        if (!this.client) return null;

        const room = this.getRoom(roomId);
        if (!room) return null;

        const currentUserId = this.getCurrentUserMatrixId();

        const readReceipt = room.getReadReceiptForUserId(currentUserId, false);
        return readReceipt?.eventId || null;
    }

    async markMessagesAsRead(roomId: string, eventId?: string): Promise<void> {
        if (!this.client) return;

        const room = this.getRoom(roomId);
        if (!room) return;

        try {
            let targetEvent;

            const timeline = room.getLiveTimeline();
            const events = timeline.getEvents();
            const currentUserId = this.getCurrentUserMatrixId();

            const isUnreadCountedEvent = (event: MatrixEvent): boolean => {
                const type = event.getType();
                // A missed call marks the room unread, so the receipt has to be
                // able to land on the invite too — otherwise opening the room
                // could never clear a call that arrived after the last message.
                if (isCallInviteEvent(event)) return event.getSender() !== currentUserId;
                if (type !== 'm.room.message' && type !== 'm.room.encrypted') return false;
                const sender = event.getSender();
                if (sender === currentUserId) return false;
                if (sender && isBotUser(sender)) return false;
                const content = event.getContent();
                if (content?.msgtype?.startsWith('org.phonehq.')) return false;
                if (isSummaryMessageContent(content)) return false;
                if (event.getId()?.startsWith('~')) return false;
                if (content?.['m.relates_to']?.rel_type === 'm.replace') return false;
                return true;
            };

            if (eventId) {
                const requestedEvent = room.findEventById(eventId);
                if (requestedEvent && isUnreadCountedEvent(requestedEvent)) {
                    targetEvent = requestedEvent;
                }
            }

            if (!targetEvent) {
                const messageEvents = events.filter(isUnreadCountedEvent);
                targetEvent = messageEvents[messageEvents.length - 1];
            }

            if (targetEvent && targetEvent.getId() && !targetEvent.getId()?.startsWith('~')) {
                await this.client.sendReadReceipt(targetEvent);
                this.emitStateChange();
            }
        } catch (error) {
            logger.error(`[WEB CHAT] Failed to send read receipt: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'read_receipt_error', roomId, error: error instanceof Error ? error.message : String(error) });
        }
    }

    /**
     * One entry per call the room's timeline knows about, keyed by both the
     * Matrix call id and the invite's event id, so a renderer holding either
     * can look the call up. Derived on read: the room already carries every
     * `m.call.*` event, including for history paginated in long afterwards.
     */
    getRoomCallEntries(roomId: string): Map<string, CallTimelineEntry> {
        const room = this.getRoom(roomId);
        if (!room) return new Map();

        try {
            const events = room.getLiveTimeline()?.getEvents() ?? [];
            return buildCallTimelineEntries(
                events as unknown as CallTimelineEvent[],
                this.getCurrentUserMatrixId(),
            );
        } catch (error) {
            logger.warn(`[WEB CHAT] getRoomCallEntries failed: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'call_entries_error', roomId });
            return new Map();
        }
    }

    getUnreadMessageCount(roomId: string): number {
        const room = this.getRoom(roomId);
        if (!room) return 0;

        const currentUserId = this.getCurrentUserMatrixId();
        if (!currentUserId) return 0;

        const isUnreadCountedEvent = (event: MatrixEvent): boolean => {
            const type = event.getType();
            if (type !== 'm.room.message' && type !== 'm.room.encrypted') return false;
            const sender = event.getSender();
            if (sender === currentUserId) return false;
            if (sender && isBotUser(sender)) return false;
            const content = event.getContent();
            if (content?.msgtype?.startsWith('org.phonehq.')) return false;
            if (isSummaryMessageContent(content)) return false;
            if (event.getId()?.startsWith('~')) return false;
            if (content?.['m.relates_to']?.rel_type === 'm.replace') return false;
            if (event.isRedacted?.()) return false;
            return true;
        };

        const events = room.getLiveTimeline().getEvents();
        const receipt = room.getReadReceiptForUserId(currentUserId, false);
        const receiptEventId = receipt?.eventId;

        let startIndex = 0;
        if (receiptEventId) {
            const idx = events.findIndex(e => e.getId() === receiptEventId);
            if (idx >= 0) startIndex = idx + 1;
        }

        // A call nobody took is an unread item of its own: it is the reason
        // the agent has to ring the customer back, and it carries no message
        // to be counted by the loop above.
        const callEntries = this.getRoomCallEntries(roomId);

        let count = 0;
        for (let i = startIndex; i < events.length; i++) {
            const event = events[i];
            if (isCallInviteEvent(event)) {
                const eventId = event.getId();
                const entry = eventId ? callEntries.get(eventId) : undefined;
                if (entry?.direction === 'incoming' && entry.outcome === 'missed') count++;
                continue;
            }
            if (isUnreadCountedEvent(event)) count++;
        }
        return count;
    }

    private emittedCallRingingEventIds = new Set<string>();
    private emittedCallEscalationEventIds = new Set<string>();

    private emitCallEscalation(event: MatrixEvent): void {
        const roomId = event.getRoomId();
        const senderId = event.getSender();
        const eventId = event.getId();
        if (!roomId || !senderId || !eventId || this.emittedCallEscalationEventIds.has(eventId)) return;
        this.emittedCallEscalationEventIds.add(eventId);
        if (this.emittedCallEscalationEventIds.size > 100) {
            const oldest = this.emittedCallEscalationEventIds.values().next().value;
            if (oldest) this.emittedCallEscalationEventIds.delete(oldest);
        }
        this.emit('callEscalation', {content: event.getContent(), roomId, senderId, eventId});
    }

    private emitCallRinging(event: MatrixEvent): void {
        const content = event.getContent();
        const roomId = event.getRoomId();
        const senderId = event.getSender();
        if (!isValidCallRingingContent(content) || !roomId || !senderId) return;
        const callId = content.call_id;
        const eventId = event.getId();
        if (eventId) {
            if (this.emittedCallRingingEventIds.has(eventId)) return;
            this.emittedCallRingingEventIds.add(eventId);
            if (this.emittedCallRingingEventIds.size > 100) {
                const oldest = this.emittedCallRingingEventIds.values().next().value;
                if (oldest) this.emittedCallRingingEventIds.delete(oldest);
            }
        }
        this.emit('callRinging', {callId, roomId, senderId, eventId: event.getId()});
    }

    private setupEventListeners() {
        if (!this.client) return;
        const eventClient = this.client;
        const isCurrentClient = () => this.client === eventClient;
        const activeCallEventFastLane = new ActiveCallEventFastLane({
            decryptEvent: (event) => eventClient.decryptEventIfNeeded(event),
            findActiveCall: (callId) => this.activeCalls.get(callId),
            getBufferedEvents: () => eventClient.callEventHandler?.callEventBuffer,
            hasActiveCallInRoom: (roomId) => Boolean(
                roomId && Array.from(this.activeCalls.values()).some((call) => call.roomId === roomId),
            ),
            isOwnEvent: (event) => event.getSender() === eventClient.getUserId(),
            notifyVoipEventReceived: (event) => eventClient.emit(ClientEvent.ReceivedVoipEvent, event),
            notifyCallRinging: (event) => this.emitCallRinging(event),
            notifyCallEscalation: (event) => this.emitCallEscalation(event),
        });

        console.log('Setting up Matrix event listeners');
        let hasCompletedInitialSync = false;

        eventClient.on(MatrixEventEvent.Replaced, (event: MatrixEvent) => {
            if (!isCurrentClient()) return;
            const roomId = event.getRoomId();
            if (!roomId) return;
            const room = eventClient.getRoom(roomId);
            if (!room) return;
            this.emit('messageUpdate', {event, room});
        });

        // Setup sliding sync callbacks
        slidingSyncManager.setOnInitialSyncComplete(() => {
            if (!isCurrentClient()) return;
            logger.info('[WEB CHAT] Initial sync complete, restoring selected room', { source: 'chat', event: 'restore_selected_room' });
            void this.restoreSelectedRoom().finally(() => {
                if (!isCurrentClient()) return;
                this.chatState.initialRoomRestoreSettled = true;
                this.emitStateChange();
                this.startRoomPreviewPrefetch(eventClient);
            });
        });

        slidingSyncManager.setOnRoomReceived((roomId: string) => {
            if (!isCurrentClient()) return;
            const room = this.getRoom(roomId);
            if (room) {
                room.loadMembersIfNeeded().catch(err => {
                    logger.warn(`[WEB CHAT] Failed to load members for received room: ${err instanceof Error ? err.message : String(err)}`, { source: 'chat', event: 'room_members_load_error', roomId, error: err instanceof Error ? err.message : String(err) });
                });
            }
            this.scheduleRoomsListUpdate();
        });

        eventClient.on(CallEventHandlerEvent.Incoming, this.onIncomingCall);
        eventClient.on('Session.logged_out' as any, (errorObj: any) => {
            if (!isCurrentClient()) return;
            logger.warn('[WEB CHAT] Session expired, tokens invalid', { source: 'chat', event: 'session_expired', error: errorObj?.message || String(errorObj) });
            this.handleTokenExpiry(errorObj);
        });

        eventClient.once(ClientEvent.Sync, (state) => {
            if (!isCurrentClient()) return;
            if (state === 'PREPARED') {
                const rooms = eventClient.getRooms();
                rooms.forEach(room => {
                    this.existingRoomIds.add(room.roomId);
                });
                this.loadMembersForIncompleteRooms();
                hasCompletedInitialSync = true;
                this.chatState.hasCompletedInitialSync = true;
            }
        });

        eventClient.on(ClientEvent.Sync, (state) => {
            if (!isCurrentClient()) return;
            this.chatState.syncState = state;

            if (state === 'PREPARED') {
                this.updateRoomsList();
                if (this.chatState.error?.includes('token') || this.chatState.error?.includes('auth')) {
                    this.chatState.error = null;
                }
            }

            this.emitStateChange();
        });

        eventClient.on(RoomEvent.Name, () => {
            if (!isCurrentClient()) return;
            this.updateRoomsList();
            this.emitStateChange();
        });

        eventClient.on(ClientEvent.Room, (room: Room) => {
            if (!isCurrentClient()) return;
            if (this.processingRooms.has(room.roomId)) {
                return;
            }

            if (hasCompletedInitialSync && !this.existingRoomIds.has(room.roomId)) {
                const currentUserId = this.getCurrentUserMatrixId();
                const member = room.getMember(currentUserId);

                if (member && member.membership === "join") {
                    const roomCreator = this.getRoomCreator(room);
                    const otherMembers = this.getRoomOtherMembers(room);

                    if (roomCreator && roomCreator !== currentUserId) {
                        this.emit('addedToNewRoom', {
                            roomId: room.roomId,
                            roomName: room.name || 'New Chat Room',
                            creatorUserId: roomCreator,
                            creatorName: this.getUserDisplayName(roomCreator),
                            otherMembers,
                            timestamp: Date.now(),
                            isDirectMessage: this.isDirectMessageRoom(room)
                        });
                    }
                }

                const createEvent = room.currentState.getStateEvents('m.room.create', '');
                if (createEvent) {
                    const roomCreationTime = createEvent.getTs();
                    const now = Date.now();
                    if (now - roomCreationTime < 10000) {
                        this.roomsInSetup.add(room.roomId);
                        logger.debug('[WEB CHAT] Room marked as in setup phase', { source: 'chat', event: 'room_setup_phase', roomId: room.roomId, roomAge: now - roomCreationTime });
                        setTimeout(() => {
                            if (!isCurrentClient()) return;
                            this.roomsInSetup.delete(room.roomId);
                            logger.debug('[WEB CHAT] Room removed from setup phase', { source: 'chat', event: 'room_setup_phase_end', roomId: room.roomId });
                        }, 10000);
                    }
                }
            }
            this.existingRoomIds.add(room.roomId);

            this.processingRooms.add(room.roomId);
            this.updateRoomsList();
            if (this.isDirectMessageRoom(room)) {
                const expectedCount =
                    (room.getJoinedMemberCount() || 0) + (room.getInvitedMemberCount() || 0);
                const currentCount = room.getMembers().length;
                if (currentCount < expectedCount) {
                    room
                        .loadMembersIfNeeded()
                        .then(() => room.recalculate())
                        .catch((error) => {
                            console.error(`Failed to load members for new room ${room.roomId}:`, error);
                        });
                }
            }
            this.emit("newRoom", room);

            if (room.hasEncryptionStateEvent()) {
                this.downloadDeviceKeysForRoom(room.roomId).catch(err => {
                    logger.warn(`[WEB CHAT] Failed to download keys for new room: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'new_room_keys_error', roomId: room.roomId, error: err instanceof Error ? err.message : String(err) }));
                });
            }

            setTimeout(() => {
                if (!isCurrentClient()) return;
                this.processingRooms.delete(room.roomId);
            }, 1000);
        });

        eventClient.on('RoomMember.membership' as any, async (event: MatrixEvent, member: any) => {
            if (!isCurrentClient()) return;
            const currentUserId = this.getCurrentUserMatrixId();
            const membership = event.getContent().membership;
            const roomId = event.getRoomId();

            if (member.userId === currentUserId && (membership === 'leave' || membership === 'ban')) {
                if (roomId && this.chatState.selectedRoomId === roomId) {
                    const joinedRooms = this.getRooms().filter(r => r.roomId !== roomId && r.getMyMembership() === 'join' && !r.isSpaceRoom());
                    const firstRoom = joinedRooms[0];
                    if (firstRoom) {
                        this.selectRoom(firstRoom.roomId);
                    } else {
                        this.selectRoom(null);
                    }
                }
            }

            if (member.userId === currentUserId && membership === 'invite') {
                const inviterUserId = event.getSender();
                const room = this.getRoom(roomId);

                if (!inviterUserId) return;

                this.emit('roomInvitation', {
                    roomId,
                    inviterUserId,
                    inviterName: this.getUserDisplayName(inviterUserId),
                    roomName: room?.name || 'New Chat Room',
                    timestamp: Date.now()
                });
            }
            if (event.getContent().is_direct) {
                if (membership === 'join' || membership === 'invite') {
                    if (!roomId) return;
                    // The creator sees the peer's invite sent by themselves: key by the counterparty.
                    const sender = event.getSender() || '';
                    const peerId = sender === currentUserId ? (event.getStateKey() || member.userId || '') : sender;
                    await this.addDirectRoomToAccountData(roomId, peerId);
                    if (!isCurrentClient()) return;
                }
            }
            if (member.userId === currentUserId && membership === 'join') {
                const room = this.getRoom(roomId);
                const prevContent = event.getPrevContent();

                if (prevContent?.membership === 'invite') {
                    this.emit('roomJoined', {
                        roomId,
                        roomName: room?.name || 'New Chat Room',
                        timestamp: Date.now()
                    });
                }
            }

            // Propagate any membership change so UI can refresh room member lists
            this.emitStateChange();
        });

        let scrollbackResetTimer: ReturnType<typeof setTimeout> | null = null;
        eventClient.on(RoomEvent.Timeline, async (event: MatrixEvent, room: Room | undefined, toStartOfTimeline?: boolean) => {
            if (!isCurrentClient()) return;
            if (!room) return;

            if (toStartOfTimeline) {
                if (room.roomId === this.chatState.selectedRoomId) {
                    if (scrollbackResetTimer) clearTimeout(scrollbackResetTimer);
                    scrollbackResetTimer = setTimeout(() => {
                        if (!isCurrentClient()) return;
                        this.emit('timelineReset', { roomId: room.roomId });
                        scrollbackResetTimer = null;
                    }, 100);
                }
                return;
            }

            const eventType = event.getType();

            if (eventType === EventType.RoomMember && room.hasEncryptionStateEvent()) {
                const membership = event.getContent().membership;
                const userId = event.getStateKey();

                if (membership === 'join' && userId && userId !== this.getCurrentUserMatrixId()) {
                    const crypto = this.client?.getCrypto();
                    if (crypto && this.chatState.isCryptoEnabled) {
                        try {
                            await crypto.getUserDeviceInfo([userId], true);
                            if (!isCurrentClient()) return;
                        } catch (error) {
                            logger.warn(`[WEB CHAT] Failed to download device keys: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({ event: 'download_device_keys_error', error: error instanceof Error ? error.message : String(error) }));
                        }
                    }
                }
            }

            if (!isCurrentClient()) return;

            if (eventType === EventType.RoomMessage || eventType === 'm.room.encrypted') {
                if (isMessageEdit(event)) {
                    return;
                }
                if (eventType === 'm.room.encrypted' && event.shouldAttemptDecryption() && eventClient.getCrypto()) {
                    eventClient.decryptEventIfNeeded(event).catch(err => {
                        logger.warn(`[WEB CHAT] Failed to decrypt live event: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'live_event_decrypt_error', roomId: room.roomId, eventId: event.getId(), error: err instanceof Error ? err.message : String(err) }));
                        if (!isCurrentClient()) return;
                        this.emit('newMessage', {event, room});
                    });
                    return;
                }
                this.emit('newMessage', {event, room});
            }

            if (eventType === 'm.room.redaction') {
                const redactedEventId =
                    (event as any)?.event?.redacts ||
                    (typeof (event as any)?.getAssociatedId === 'function' ? (event as any).getAssociatedId() : undefined) ||
                    event.getContent()?.redacts;

                if (redactedEventId) {
                    this.emit('messageDelete', { eventId: redactedEventId, roomId: room.roomId });
                } else {
                    // Fallback when target id is not available: force timeline reload for the room.
                    this.emit('timelineReset', { roomId: room.roomId });
                }
            }

            if (eventType === 'm.room.member' ||
                eventType === 'm.room.name' ||
                eventType === 'm.room.topic') {
                this.emit('newMessage', {event, room});
            }

            // A call's answer, candidates and hangup never become messages, but
            // they decide what its row says — and the invite is the row itself.
            if (isCallTimelineEvent(event)) {
                this.emit('callEvent', {event, room});
            }

            if (eventType === RTC_PROTOCOL_V1.notificationEventType) {
                this.emit('newMessage', {event, room});
                this.emit('rtcNotification', {event, room});
            }

        });

        eventClient.on('Room.receipt' as any, (event: MatrixEvent) => {
            if (!isCurrentClient()) return;
            const currentUserId = this.getCurrentUserMatrixId();
            const content = event.getContent();

            for (const eventId in content) {
                const receipts = content[eventId]['m.read'];
                if (receipts && receipts[currentUserId]) {
                    this.emitStateChange();
                    break;
                }
            }
        });

        eventClient.on('Room.unreadNotifications' as any, () => {
            if (!isCurrentClient()) return;
            this.emitStateChange();
        });

        eventClient.on('RoomMember.typing' as any, (event: MatrixEvent) => {
            if (!isCurrentClient()) return;
            this.emit('typing', {event});
        });

        eventClient.on(ClientEvent.SyncUnexpectedError, (error: Error) => {
            if (!isCurrentClient()) return;
            logger.error(`[WEB CHAT] Sync unexpected error: ${error.message}`, { source: 'chat', event: 'sync_error', error: error.message });

            if (this.isAuthenticationError(error)) {
                this.handleTokenExpiry(error);
                return;
            }

            this.chatState.error = `Sync error: ${error.message}`;
            this.emitStateChange();
        });


        eventClient.on(ClientEvent.AccountData, (event: MatrixEvent) => {
            if (!isCurrentClient()) return;
            if (event.getType() === EventType.Direct) {
                this.emitStateChange();
            }
        });

        eventClient.on('RoomState.events' as any, (event: MatrixEvent) => {
            if (!isCurrentClient()) return;
            if (shouldRefreshRoomState(event.getType())) {
                this.emitStateChange();
            }

            if (event.getType() === EventType.RoomEncryption) {
                const roomId = event.getRoomId();
                if (roomId) {
                    this.roomsPendingEncryption.delete(roomId);
                    this.downloadDeviceKeysForRoom(roomId).catch(err => {
                        logger.warn(`[WEB CHAT] Failed to download keys after encryption enabled: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'encryption_enabled_keys_error', roomId, error: err instanceof Error ? err.message : String(err) }));
                    });
                }
            }
        });

        eventClient.on('http_error' as any, (error: any) => {
            if (!isCurrentClient()) return;
            if (this.isAuthenticationError(error)) {
                logger.warn(`[WEB CHAT] HTTP auth error: ${error.httpStatus}`, { source: 'chat', event: 'http_auth_error', httpStatus: error.httpStatus, error: error?.message || String(error) });
                this.handleTokenExpiry(error);
            }
        });
        eventClient.on("Room.timeline" as any, (event: MatrixEvent) => {
            if (!isCurrentClient()) return;

            const receivedAt = Date.now();
            void activeCallEventFastLane.handle(event).then((handled) => {
                if (!handled || !isCurrentClient()) return;

                logger.info("[WEB CHAT] Active call event handled before completed sync", {
                    source: "matrix-call",
                    event: "active_call_event_fast_lane",
                    eventId: event.getId(),
                    eventType: event.getType(),
                    callId: event.getContent().call_id,
                    processingMs: Date.now() - receivedAt,
                });
            }).catch((error) => {
                logger.warn(
                    `[WEB CHAT] Active call event fast lane failed: ${error instanceof Error ? error.message : String(error)}`,
                    {
                        source: "matrix-call",
                        event: "active_call_event_fast_lane_error",
                        eventId: event.getId(),
                        error: error instanceof Error ? error.message : String(error),
                    },
                );
            });

            if (event.getType() === MATRIX_CALL_METADATA_EVENT_TYPE) {
                const metadataContent = readMatrixCallMetadataContent(event.getContent());

                if (metadataContent) {
                    store.dispatch(setCallMetadata(metadataContent));
                }
            }
        });

    }

    private mDirectWriteChain: Promise<void> = Promise.resolve();
    private pendingDirectWrites = new Map<string, Promise<void>>();

    public addDirectRoomToAccountData(roomId: string, otherUserId: string): Promise<void> {
      // The same room/peer pair queued or in flight shares one task: the self-heal re-enqueues per room on every state change.
      const key = `${roomId}|${otherUserId}`;
      const existing = this.pendingDirectWrites.get(key);
      if (existing) return existing;

      const client = this.client;
      const task = () => this.writeDirectRoomIfMissing(client, roomId, otherUserId);
      const run = this.mDirectWriteChain.then(task, task);
      this.mDirectWriteChain = run;
      const tracked = run.finally(() => {
        if (this.pendingDirectWrites.get(key) === tracked) this.pendingDirectWrites.delete(key);
      });
      this.pendingDirectWrites.set(key, tracked);
      return tracked;
    }

    /** Fresh GET of m.direct (getAccountDataFromServer serves the local store after initial sync). Null when unreadable. */
    private async fetchServerDirectMap(client: MatrixClient): Promise<Record<string, string[]> | null> {
      try {
        const path = `/user/${encodeURIComponent(client.getUserId() || '')}/account_data/${EventType.Direct}`;
        const content = await client.http.authedRequest<unknown>(Method.Get, path);
        if (!content || typeof content !== 'object' || Array.isArray(content)) return null;
        const normalised: Record<string, string[]> = {};
        for (const [peer, rooms] of Object.entries(content as Record<string, unknown>)) {
          normalised[peer] = Array.isArray(rooms) ? rooms.filter((r): r is string => typeof r === 'string') : [];
        }
        return normalised;
      } catch (e) {
        const err = e as { httpStatus?: number; errcode?: string; data?: { errcode?: string } };
        if (err?.errcode === 'M_NOT_FOUND' || err?.data?.errcode === 'M_NOT_FOUND') return {};
        return null;
      }
    }

    private async writeDirectRoomIfMissing(client: MatrixClient | null, roomId: string, otherUserId: string): Promise<void> {
      try {
        if (!client || this.client !== client) {
          // The client that queued this write has since been torn down/replaced
          // (e.g. logout or a fast re-init) — writing against a stale or a
          // different session's client would corrupt account data across sessions.
          return;
        }

        const selfUserId = client.getUserId() || '';
        if (!otherUserId || otherUserId === selfUserId) {
          // Keying m.direct by our own id records a DM "with ourselves".
          return;
        }

        // Server copy ONLY: the local cache is empty until sliding sync delivers
        // account data, and rewriting the blob from it wipes every other DM.
        const serverMap = await this.fetchServerDirectMap(client);
        if (!serverMap) {
          logger.warn(`[WEB CHAT] Skipping m.direct write: could not read the server copy`, { source: 'chat', event: 'direct_room_account_data_skipped', roomId });
          return;
        }
        if (this.client !== client) return;

        if (serverMap[otherUserId]?.includes(roomId)) {
          return;
        }

        const newMap: Record<string, string[]> = {
          ...serverMap,
          [otherUserId]: Array.from(new Set([...(serverMap[otherUserId] || []), roomId])),
        };

        // Raw PUT, awaited to completion: the chain must not move on while this write is in flight,
        // or the next read misses this room and an out-of-order PUT drops it.
        await client.setAccountDataRaw(EventType.Direct, newMap);
      } catch (error) {
        logger.error(`[WEB CHAT] Failed to add direct room to account data: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'direct_room_account_data_error', roomId, error: error instanceof Error ? error.message : String(error) });
      }
    }

    private setupCallEventListeners(call: MatrixCall): void {
        const callId = call.callId;
        console.log(`Setting up event listeners for call ${callId}`);

        call.on(CallEvent.State, (newState: CallState, oldState: CallState) => {
            console.log(`Call ${callId} state changed: ${oldState} -> ${newState}`);
            this.emit('callStateChanged', {
                callId,
                newState,
                oldState,
                roomId: call.roomId,
                hangupParty: call.hangupParty,
                hangupReason: call.hangupReason,
            });
            switch (newState) {
                case CallState.Connected:
                    console.log(`Call ${callId} connected successfully`);
                    this.emit('callConnected', {callId, roomId: call.roomId});
                    break;

                case CallState.Ended:
                    console.log(
                        `Call ${callId} ended`,
                        {hangupParty: call.hangupParty, hangupReason: call.hangupReason},
                    );

                    this.activeCalls.delete(callId);
                    this.incomingCalls.delete(callId);
                    this.emit('callEnded', {
                        callId,
                        roomId: call.roomId,
                        reason: call.hangupReason,
                        hangupParty: call.hangupParty,
                    });
                    break;

                case CallState.Ringing:
                    console.log(`Call ${callId} is ringing`);
                    break;

                case CallState.Connecting:
                    console.log(`Call ${callId} is connecting`);
                    break;
            }
        });

        call.on(CallEvent.Error, (error: any) => {
            console.error(`Call ${callId} error:`, error);

            this.emit('callError', {
                callId,
                error: error.message || 'Unknown call error',
                code: error.code,
                roomId: call.roomId
            });
        });

        call.on(CallEvent.FeedsChanged, () => {
            console.log(`Call ${callId} feeds changed`);

            this.voiceHandler.handleCallFeedsChanged(callId, call);

            const localFeeds = call.getLocalFeeds();
            const remoteFeeds = call.getRemoteFeeds();

            this.emit('callFeedsChanged', {
                callId,
                localFeeds,
                remoteFeeds,
                roomId: call.roomId
            });
        });

        call.on(CallEvent.Hangup, (endedCall: MatrixCall) => {
            this.activeCalls.delete(callId);
            this.incomingCalls.delete(callId);

            this.emit('callHungUp', {
                callId,
                roomId: call.roomId,
                reason: endedCall.hangupReason,
                hangupParty: endedCall.hangupParty,
            });
        });

        call.on(CallEvent.Replaced, (newCall: MatrixCall) => {
            console.log(`Call ${callId} replaced by ${newCall.callId}`);

            this.activeCalls.delete(callId);
            this.incomingCalls.delete(callId);

            this.activeCalls.set(newCall.callId, newCall);
            this.setupCallEventListeners(newCall);

            this.emit('callReplaced', {
                oldCallId: callId,
                newCallId: newCall.callId,
                roomId: call.roomId,
                call: newCall,
            });
        });


    }
    private onIncomingCall = (call: MatrixCall): void => {
        this.incomingCalls.set(call.callId, call);

        store.dispatch(setDialerHidden(false));
        store.dispatch(setIncomingCall(true));

        this.setupCallEventListeners(call);
        const isVideo = call.type === 'video';

        const room = this.getRoom(call.roomId);
        const callerUserId = call.getOpponentMember()?.userId;
        this.emit('incomingCall', {
            callId: call.callId,
            roomId: call.roomId,
            callerUserId,
            callerName: this.getUserDisplayName(callerUserId || ''),
            isVideo,
            call,
            room,
        });
    };
    async answerCall(callId: string, withVideo: boolean = false): Promise<void> {
        try {
            const call = this.incomingCalls.get(callId);

            if (!call) {
               return;
            }

            this.incomingCalls.delete(callId);
            this.activeCalls.set(callId, call);

            if (call.type === CallType.Video) {
                await call.answer(true, withVideo);
            } else {
                await call.answer();
            }

            this.emit('callAnswered', {
                callId,
                isVideo: withVideo,
                call,
                roomId: call.roomId
            });

        } catch (error) {
            console.error('Failed to answer call:', error);

            this.incomingCalls.delete(callId);
            this.activeCalls.delete(callId);

            this.emit('callError', {
                callId,
                error: error instanceof Error ? error.message : 'Unknown error',
                action: 'answer'
            });

            throw error;
        }
    }

    async rejectCall(callId: string): Promise<void> {
        try {
            const call = this.incomingCalls.get(callId);

            if (!call) {
                throw new Error(`Incoming call ${callId} not found`);
            }

            console.log(`Rejecting call ${callId}`);

            call.reject();

            this.incomingCalls.delete(callId);

            this.emit('callRejected', {
                callId,
                roomId: call.roomId
            });

        } catch (error) {
            console.error('Failed to reject call:', error);
            throw error;
        }
    }
    async rejectCallAsBusy(callId: string): Promise<void> {
        const call = this.incomingCalls.get(callId);
        if (!call?.roomId || !this.client) {
            throw new Error(`Incoming call ${callId} not found`);
        }
        this.incomingCalls.delete(callId);
        await this.client.sendEvent(call.roomId, EventType.CallReject, {
            version: '1',
            call_id: call.callId,
            party_id: call.ourPartyId,
            reason: CallErrorCode.UserBusy,
        });
        this.emit('callRejected', {
            callId,
            roomId: call.roomId
        });
    }

    async sendCallHangup(call: MatrixCall, reason: CallErrorCode): Promise<void> {
        await this.sendCallTermination(call, EventType.CallHangup, reason);
    }

    async sendCallReject(call: MatrixCall, reason: CallErrorCode): Promise<void> {
        await this.sendCallTermination(call, EventType.CallReject, reason);
    }

    private async sendCallTermination(
        call: MatrixCall,
        type: EventType.CallHangup | EventType.CallReject,
        reason: CallErrorCode,
    ): Promise<void> {
        if (!this.client) {
            throw new Error(`Matrix client unavailable for call ${call.callId}`);
        }
        await this.client.sendEvent(call.roomId, type, {
            version: '1',
            call_id: call.callId,
            party_id: call.ourPartyId,
            reason,
        });
    }

    async hangupCall(callId: string): Promise<void> {
        try {
            let call = this.activeCalls.get(callId);

            if (!call) {
                call = this.incomingCalls.get(callId);
            }

            if (!call) {
                console.warn(`Call ${callId} not found in active or incoming calls`);
                throw new Error(`Call ${callId} not found`);
            }


            call.hangup(CallErrorCode.UserHangup, false);

            this.activeCalls.delete(callId);
            this.incomingCalls.delete(callId);

            this.emit('callHungUp', {
                callId,
                roomId: call.roomId
            });

            console.log(`Call ${callId} hung up successfully`);

        } catch (error) {
            console.error('Failed to hangup call:', error);

            this.emit('callError', {
                callId,
                error: error instanceof Error ? error.message : 'Unknown error',
                action: 'hangup'
            });

            throw error;
        }
    }
    prepareCall(roomId: string): MatrixCall {
        const call = this.client?.createCall(roomId);
        if (!call) {
            throw new Error('Browser does not support calling');
        }
        store.dispatch(setDialerHidden(false));
        store.dispatch(setIncomingCall(true));

        const callId = call.callId;

        this.activeCalls.set(callId, call);

        this.setupCallEventListeners(call);

        return call;
    }

    async placePreparedCall(call: MatrixCall, isVideo: boolean = false): Promise<MatrixCall> {
        const callId = call.callId;

        try {
            if (isVideo) {
                await call.placeVideoCall();
            } else {
                await call.placeVoiceCall();
            }


            this.emit('callStarted', {
                callId,
                roomId: call.roomId,
                isVideo,
                call
            });

            return call;

        } catch (error) {
            console.error(`Failed to place call ${callId}:`, error);

            this.activeCalls.delete(callId);

            this.emit('callError', {
                callId,
                error: error instanceof Error ? error.message : 'Unknown error',
                action: 'start',
                roomId: call.roomId,
            });

            throw error;
        }
    }

    async startCall(roomId: string, isVideo: boolean = false): Promise<MatrixCall> {
        const call = this.prepareCall(roomId);
        return this.placePreparedCall(call, isVideo);
    }

    private getRoomCreator(room: Room): string | null {
        try {
            const createEvent = room.currentState.getStateEvents('m.room.create', '');
            if (createEvent && createEvent.getSender()) {
                return createEvent.getSender() || null;
            }

            const timeline = room.getLiveTimeline();
            const events = timeline.getEvents();
            const memberEvents = events.filter(event =>
                event.getType() === 'm.room.member' &&
                event.getContent().membership === 'join'
            ).sort((a, b) => a.getTs() - b.getTs());

            if (memberEvents.length > 0) {
                return memberEvents[0].getSender() || null;
            }

            return null;
        } catch (error) {
            logger.error(`[WEB CHAT] Error getting room creator: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'room_creator_error', error: error instanceof Error ? error.message : String(error) });
            return null;
        }
    }

    public isDirectMessageRoom = (room: Room): boolean => {
        if (!room?.roomId) return false;

        // Check m.direct account data
        const directData = this.client?.getAccountData(EventType.Direct);
        if (directData) {
            const directRooms = directData.getContent();
            for (const userId in directRooms) {
                const roomIds = Array.isArray(directRooms[userId])
                    ? directRooms[userId]
                    : [...directRooms[userId]];

                if (roomIds.includes(room.roomId)) {
                    return true;
                }
            }
        }

        // Fallback: check member events for is_direct flag (handles sliding sync timing)
        const memberEvents = room.currentState?.getStateEvents('m.room.member') || [];
        for (const memberEvent of memberEvents) {
            if (memberEvent?.getContent()?.is_direct || memberEvent?.getPrevContent()?.is_direct) {
                return true;
            }
        }

        return false;
    }

    private getUserDisplayName(userId: string): string {
        const rooms = this.getRooms();
        for (const room of rooms) {
            const member = room.getMember(userId);
            if (member && member.rawDisplayName && member.rawDisplayName !== userId) {
                return member.rawDisplayName;
            }
        }

        return userId.split(':')[0].replace('@', '');
    }

    directPeerOf(roomId: string): string | undefined {
        const room = this.client?.getRoom(roomId);
        const currentUserId = this.getCurrentUserMatrixId();
        if (!room) return undefined;
        const others = room.getMembers()
            .filter(member => member.userId !== currentUserId && (member.membership === 'join' || member.membership === 'invite'))
            .map(member => member.userId);
        return others.length === 1 ? others[0] : undefined;
    }

    private getRoomOtherMembers(room: Room): Array<{ userId: string, displayName: string }> {
        const currentUserId = this.getCurrentUserMatrixId();
        return room.getJoinedMembers()
            .filter(member => member.userId !== currentUserId)
            .map(member => ({
                userId: member.userId,
                displayName: member.rawDisplayName || this.getUserDisplayName(member.userId)
            }));
    }

    private updateRoomsList() {
        if (!this.client) return;

        const rooms = this.client.getRooms();

        this.chatState.rooms = [...rooms];
        this.emitStateChange();
    }

    private scheduleRoomsListUpdate(): void {
        this.pendingRoomDataCount += 1;

        if (this.roomsListUpdateTimeout !== null) return;

        this.roomsListUpdateTimeout = window.setTimeout(() => {
            this.roomsListUpdateTimeout = null;
            this.pendingRoomDataCount = 0;
            this.updateRoomsList();
        }, 150);
    }

    private requestWhoami(token: string, signal?: AbortSignal): Promise<Response> {
        const baseUrl = window.__ENV__?.SYNAPSE_BASE_URL;
        const authorizationHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        return fetch(`${baseUrl}/_matrix/client/v3/account/whoami`, {
            headers: {'Authorization': authorizationHeader},
            signal,
        });
    }

    private async extractUserIdFromToken(
        token: string,
        signal?: AbortSignal,
    ): Promise<{ device_id: string, user_id: string }> {
        const response = await this.requestWhoami(token, signal);

        if (!response.ok) {
            throw new Error(`Failed to get user ID: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    private async getPassphraseFromBackend(signal?: AbortSignal): Promise<PassphraseResponse> {
        const baseUrl = window.__ENV__?.URL_WEBAPP_API;
        const userId = store.getState().auth.userId;
        const tenant = store.getState().auth.tenant;

        const response = await authenticatedFetch(`${baseUrl}/api/v1/chats/${userId}/passphrases`, {
            method: 'GET',
            headers: {
                'Tenant': tenant || '',
                'Content-Type': 'application/json'
            },
            signal,
        });

        if (!response.ok) {
            throw new Error(`Failed to get passphrase from backend: ${response.status}`);
        }

        const data = await response.json();

        if (!data.passphrase) {
            throw new Error('Invalid passphrase response from backend');
        }

        return data;
    }

    /**
     * Manually store PBKDF2 parameters in Matrix account data
     * This ensures cross-platform recovery works (mobile ↔ web)
     *
     * The SDK's bootstrapSecretStorage() doesn't always store these params properly,
     * so we store them manually to ensure mobile can derive the same SSK.
     */
    private async storePBKDF2ParamsManually(
        setupClient: MatrixClient,
        recoveryKey: any,
        generation: number,
    ): Promise<void> {
        try {
            this.assertSessionCurrent(generation, setupClient);
            logger.info('[WEB CHAT] storePBKDF2Params: checking if PBKDF2 params need to be stored', this.encCtx({ event: 'pbkdf2_check' }));

            const defaultKeyData = await setupClient.getAccountData('m.secret_storage.default_key');
            this.assertSessionCurrent(generation, setupClient);

            if (!defaultKeyData || !defaultKeyData.getContent()?.key) {
                logger.warn('[WEB CHAT] storePBKDF2Params: no default key found after bootstrapSecretStorage', this.encCtx({ event: 'pbkdf2_no_default_key' }));
                return;
            }

            const keyId = defaultKeyData.getContent().key;

            const keyInfoData = await setupClient.getAccountData(`m.secret_storage.key.${keyId}`);
            this.assertSessionCurrent(generation, setupClient);
            const existingKeyInfo = keyInfoData?.getContent();

            if (existingKeyInfo?.passphrase?.algorithm === 'm.pbkdf2') {
                logger.info('[WEB CHAT] storePBKDF2Params: PBKDF2 params already stored by SDK', this.encCtx({
                    event: 'pbkdf2_already_stored',
                    keyId,
                    iterations: existingKeyInfo.passphrase.iterations,
                }));
                return;
            }

            logger.warn('[WEB CHAT] storePBKDF2Params: SDK did not store PBKDF2 params, storing manually', this.encCtx({
                event: 'pbkdf2_manual_store',
                keyId,
            }));

            if (!recoveryKey.keyInfo?.passphrase) {
                logger.error('[WEB CHAT] storePBKDF2Params: recovery key has no passphrase info', this.encCtx({ event: 'pbkdf2_no_passphrase_info', keyId }));
                return;
            }

            const passphraseInfo = recoveryKey.keyInfo.passphrase;

            const keyInfo: any = {
                name: recoveryKey.keyInfo.name || 'recovery_key',
                algorithm: 'm.secret_storage.v1.aes-hmac-sha2',
                passphrase: passphraseInfo
            };

            if (existingKeyInfo?.iv) {
                keyInfo.iv = existingKeyInfo.iv;
            }
            if (existingKeyInfo?.mac) {
                keyInfo.mac = existingKeyInfo.mac;
            }

            await setupClient.setAccountData(`m.secret_storage.key.${keyId}`, keyInfo);
            this.assertSessionCurrent(generation, setupClient);

            logger.info('[WEB CHAT] storePBKDF2Params: successfully stored PBKDF2 params', this.encCtx({ event: 'pbkdf2_stored', keyId }));
        } catch (error) {
            if (!this.isSessionCurrent(generation, setupClient)) throw error;
            logger.error(`[WEB CHAT] storePBKDF2Params failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'pbkdf2_error',
                error: error instanceof Error ? error.message : String(error),
            }));
            // Don't throw - this is a non-critical enhancement
        }
    }

    private handleTokenExpiry(error?: any) {
        const session = this.currentTokenSession();
        if (!session) {
            this.softLogoutAfterTokenExpiry(error);
            return;
        }
        if (this.tokenExpiryRecoveryInFlight) return;

        const recovery = this.recoverFromTokenExpiry(session, error)
            .finally(() => {
                if (this.tokenExpiryRecoveryInFlight === recovery) {
                    this.tokenExpiryRecoveryInFlight = null;
                }
            });
        this.tokenExpiryRecoveryInFlight = recovery;
    }

    private async recoverFromTokenExpiry(session: MatrixTokenSession, error?: unknown): Promise<void> {
        try {
            await this.refreshTokens(session);
        } catch (refreshError) {
            if (!this.isTokenSessionCurrent(session)) return;
            logger.warn('[WEB CHAT] Matrix token refresh after expiry failed; signing out of chat', {
                source: 'chat',
                event: 'matrix_token_expiry_recovery_failed',
                reason: 'refresh_failed',
                error: refreshError instanceof Error ? refreshError.message : String(refreshError),
            });
            this.softLogoutAfterTokenExpiry(error);
            return;
        }

        if (!this.isTokenSessionCurrent(session)) return;
        if (this.isSyncStoppedByUnknownToken(session.client)) {
            logger.warn('[WEB CHAT] Matrix sync stopped on the expired token; signing out of chat to restart it', {
                source: 'chat',
                event: 'matrix_token_expiry_recovery_failed',
                reason: 'sync_stopped',
            });
            this.softLogoutAfterTokenExpiry(error);
            return;
        }

        logger.info('[WEB CHAT] Matrix access token refreshed after the server rejected it', {
            source: 'chat',
            event: 'matrix_token_expiry_recovered',
        });
    }

    private isSyncStoppedByUnknownToken(client: MatrixClient): boolean {
        if (client.getSyncState() !== SyncState.Error) return false;
        const syncError = client.getSyncStateData()?.error;
        return syncError instanceof MatrixError && syncError.errcode === 'M_UNKNOWN_TOKEN';
    }

    private softLogoutAfterTokenExpiry(error?: unknown) {
        // Treat token expiry as a soft logout. Keep the device id together
        // with its Rust Crypto store so reauthentication can resume the same
        // Matrix device and decrypt events received while signed out.
        const logout = this.logout();
        const logoutGeneration = this.sessionGeneration;
        void logout
            .catch((logoutError) => {
                logger.error('[WEB CHAT] Failed to clean up expired Matrix session', this.encCtx({
                    event: 'matrix_expired_cleanup_failed',
                    error: logoutError instanceof Error ? logoutError.message : String(logoutError),
                }));
            })
            .finally(() => {
                if (this.sessionGeneration !== logoutGeneration) return;
                this.chatState.error = 'Authentication expired. Please login again.';
                this.emitStateChange();
                this.emit('authenticationExpired', error);
            });
    }

    private isAuthenticationError(error: any): boolean {
        const errorMessage = error.message?.toLowerCase() || '';
        const errorData = error.data || {};
        const errcode = errorData.errcode || error.errcode;

        return (
            error?.httpStatus === 401 ||
            errorMessage.includes('unauthorized') ||
            errorMessage.includes('invalid token') ||
            errcode === 'M_UNKNOWN_TOKEN' ||
            errcode === 'M_MISSING_TOKEN'
        );
    }

    async sendMessage(roomId: string, message: string, mentions?: Array<{
        userId: string,
        displayName: string
    }>, customTag?: string, roomMentions?: Array<{
        roomId: string,
        name: string
    }>): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        const room = this.client.getRoom(roomId);
        if (!room) {
            this.updateRoomsList();
            throw new Error('Room not found');
        }

        await this.ensureRoomEncryptionReady(roomId);
        const isFirstSendAfterCreate = await this.ensureRoomMembersReady(roomId);
        if (isFirstSendAfterCreate) {
            await this.ensureMemberDeviceKeysForSend(roomId);
        }

        const isEncrypted = room.hasEncryptionStateEvent();
        try {
            const content: any = buildTextMessageContent(message, mentions ?? [], roomMentions ?? []);
            if (customTag) {
                content[customTag] = customTag;
            }

            await this.client.sendMessage(roomId, content);
            this.roomPendingMemberIds.delete(roomId);

        } catch (error) {
            logger.error(`[WEB CHAT] sendMessage failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'send_message_error',
                roomId,
                isEncrypted,
                error: error instanceof Error ? error.message : String(error),
            }));
            throw error;
        }
    }

    /**
     * Sends a forwarded copy of a message (D-CHAT-04): `content` comes from
     * `buildForwardContent`, so it already carries `org.phonehq.forwarded`.
     */
    async forwardMessage(roomId: string, content: Record<string, unknown>): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');
        const room = this.client.getRoom(roomId);
        if (!room) throw new Error('Room not found');

        await this.ensureRoomEncryptionReady(roomId);
        const isFirstSendAfterCreate = await this.ensureRoomMembersReady(roomId);
        if (isFirstSendAfterCreate) {
            await this.ensureMemberDeviceKeysForSend(roomId);
        }
        try {
            await this.client.sendMessage(roomId, content as unknown as RoomMessageEventContent);
        } catch (error) {
            logger.error(`[WEB CHAT] forwardMessage failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'forward_message_error',
                roomId,
                isEncrypted: room.hasEncryptionStateEvent(),
                error: error instanceof Error ? error.message : String(error),
            }));
            throw error;
        }
    }

    async sendPrivateReplyMessage(roomId: string, message: string, source: PrivateReplySource, mentions?: Array<{
        userId: string,
        displayName: string
    }>, roomMentions?: Array<{
        roomId: string,
        name: string
    }>): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        const room = this.client.getRoom(roomId);
        if (!room) {
            this.updateRoomsList();
            throw new Error('Room not found');
        }

        await this.ensureRoomEncryptionReady(roomId);
        const isFirstSendAfterCreate = await this.ensureRoomMembersReady(roomId);
        if (isFirstSendAfterCreate) {
            await this.ensureMemberDeviceKeysForSend(roomId);
        }

        const isEncrypted = room.hasEncryptionStateEvent();
        try {
            const composed = composePrivateReplyMessage(message, source, mentions ?? [], roomMentions ?? []);
            const content = {
                msgtype: MsgType.Text,
                body: composed.body,
                ...(composed.formattedBody !== undefined
                    ? {format: composed.format, formatted_body: composed.formattedBody}
                    : {}),
                ...(mentions && mentions.length > 0
                    ? {"m.mentions": {user_ids: mentions.map(m => m.userId)}}
                    : {}),
            } as RoomMessageEventContent;

            await this.client.sendMessage(roomId, content);
            this.roomPendingMemberIds.delete(roomId);

        } catch (error) {
            logger.error(`[WEB CHAT] sendPrivateReplyMessage failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'send_private_reply_error',
                roomId,
                isEncrypted,
                error: error instanceof Error ? error.message : String(error),
            }));
            throw error;
        }
    }

    async sendAiRequest(roomId: string, content: AiRequestContent): Promise<string> {
        if (!this.client) throw new Error('Client not initialized');
        await this.ensureRoomEncryptionReady(roomId);
        if (await this.ensureRoomMembersReady(roomId)) {
            await this.ensureMemberDeviceKeysForSend(roomId);
        }
        return deliverAiRequest(this.client, roomId, content);
    }

    async sendTranscriptionRequest(roomId: string, audioEventId: string, metadata: AiRequestMetadata = newAiRequestMetadata()): Promise<string> {
        return this.sendAiRequest(roomId, buildAiRequest({kind: 'transcription', audioEventId}, metadata));
    }

    async sendSummaryRequest(roomId: string, timeRange: string, metadata: AiRequestMetadata = newAiRequestMetadata()): Promise<string> {
        if (!isSummaryPeriod(timeRange)) throw new Error('Unsupported summary period');
        return this.sendAiRequest(roomId, buildAiRequest({kind: 'summary', period: timeRange}, metadata));
    }

    async sendAiStatusRequest(roomId: string, kind: AiRequestKind, requestEventId: string): Promise<string> {
        return this.sendAiRequest(roomId, buildAiStatusRequest(kind, requestEventId));
    }

    async inviteAiBotToRoom(roomId: string): Promise<AiBotRoomOperationResponse> {
        return this.updateAiBotRoom({roomId, operationPath: 'invite', method: 'POST'});
    }

    async repairAiBotRoomPolicy(roomId: string): Promise<AiBotRoomOperationResponse> {
        return this.updateAiBotRoom({roomId, operationPath: 'policy', method: 'PUT'});
    }

    async sendReplyMessage(roomId: string, message: string, replyToEventId: string, mentions?: Array<{
        userId: string,
        displayName: string
    }>, roomMentions?: Array<{
        roomId: string,
        name: string
    }>): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        const room = this.client.getRoom(roomId);
        if (!room) {
            this.updateRoomsList();
            throw new Error('Room not found');
        }

        await this.ensureRoomEncryptionReady(roomId);
        if (await this.ensureRoomMembersReady(roomId)) {
            await this.ensureMemberDeviceKeysForSend(roomId);
        }

        const originalEvent = room.findEventById(replyToEventId);
        if (!originalEvent) {
            throw new Error('Original message not found');
        }

        const isEncrypted = room.hasEncryptionStateEvent();

        try {
            const replyContent: any = buildReplyMessageContent(message, replyToEventId, mentions ?? [], roomMentions ?? []);

            await this.client.sendMessage(roomId, replyContent);
            this.roomPendingMemberIds.delete(roomId);

        } catch (error) {
            logger.error(`[WEB CHAT] sendReplyMessage failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'send_reply_error',
                roomId,
                isEncrypted,
                error: error instanceof Error ? error.message : String(error),
            }));
            throw error;
        }
    }


    async sendEditMessage(roomId: string, targetEventId: string, newText: string, mentions?: Array<{
        userId: string,
        displayName: string
    }>, roomMentions?: Array<{
        roomId: string,
        name: string
    }>): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        const room = this.client.getRoom(roomId);
        if (!room) {
            this.updateRoomsList();
            throw new Error('Room not found');
        }

        const text = newText.trim();
        if (!text) throw new Error('Edited message must not be empty');

        const originalEvent = room.findEventById(targetEventId);
        if (!originalEvent) {
            throw new Error('Original message not found');
        }

        if (!isEditableMessage(originalEvent, {
            currentUserId: this.getCurrentUserMatrixId(),
            isRedacted: originalEvent.isRedacted(),
        })) {
            throw new Error('Message cannot be edited');
        }

        await this.ensureRoomEncryptionReady(roomId);
        if (await this.ensureRoomMembersReady(roomId)) {
            await this.ensureMemberDeviceKeysForSend(roomId);
        }

        const isEncrypted = room.hasEncryptionStateEvent();
        const originalEventId = resolveEditTargetEventId(originalEvent);
        if (!originalEventId) {
            throw new Error('Original message not found');
        }

        const originalMentionedUserIds = originalEvent.getContent()?.['m.mentions']?.user_ids ?? [];

        try {
            await this.client.sendMessage(roomId, buildEditContent(originalEventId, text, mentions ?? [], roomMentions ?? [], originalMentionedUserIds) as unknown as RoomMessageEventContent);
            this.roomPendingMemberIds.delete(roomId);
        } catch (error) {
            logger.error(`[WEB CHAT] sendEditMessage failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'send_edit_error',
                roomId,
                isEncrypted,
                error: error instanceof Error ? error.message : String(error),
            }));
            throw error;
        }
    }

    async sendReaction(roomId: string, eventId: string, reaction: string): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        try {
            await this.client.sendEvent(roomId, 'm.reaction' as any, {
                'm.relates_to': {
                    rel_type: 'm.annotation',
                    event_id: eventId,
                    key: reaction,
                },
            });
        } catch (error) {
            logger.error(`[WEB CHAT] Failed to send reaction: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'send_reaction_error', roomId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async removeReaction(roomId: string, eventId: string, emoji: string): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        try {
            const room = this.client.getRoom(roomId);
            if (!room) throw new Error('Room not found');

            const timeline = room.getLiveTimeline();
            const events = timeline.getEvents();

            const currentUserId = this.getCurrentUserMatrixId();

            const reactionEvent = events.find(event =>
                event.getType() === 'm.reaction' &&
                event.getSender() === currentUserId &&
                event.getContent()?.['m.relates_to']?.event_id === eventId &&
                event.getContent()?.['m.relates_to']?.key === emoji &&
                !event.isRedacted()
            );

            if (reactionEvent && reactionEvent.getId()) {
                await this.client.redactEvent(roomId, reactionEvent.getId()!);
                this.emit('reactionRemoved', {roomId, eventId, emoji});
            }
        } catch (error) {
            logger.error(`[WEB CHAT] Failed to remove reaction: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'remove_reaction_error', roomId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async deleteMessage(roomId: string, eventId: string): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        try {
            await this.client.redactEvent(roomId, eventId);
        } catch (error) {
            console.error('❌ Failed to delete message:', error);
            throw error;
        }
    }

    async createRoomAsChild(
        options: {
            name?: string;
            topic?: string;
            invite?: string[];
            isDirectMessage?: boolean;
        },
        parentRoomId: string,
        onInviteProgress?: (invited: number, total: number) => void
    ): Promise<string> {
        if (!this.client) throw new Error('Client not initialized');
        const MAX_INITIAL_INVITES = 10;
        const allInvites = options.invite || [];
        const initialInvites = allInvites.slice(0, MAX_INITIAL_INVITES);
        const remainingInvites = allInvites.slice(MAX_INITIAL_INVITES);
        const currentMatrixUserId = this.client.getUserId();
        const botUserId = currentMatrixUserId ? getBotUserId(currentMatrixUserId) : null;
        if (!currentMatrixUserId || !botUserId) throw new Error('Cannot determine PhoneHQ AI Matrix user ID');

        try {
            const capabilities = await this.client.getCapabilities();
            const roomVersion = capabilities['m.room_versions']?.default;
            if (!roomVersion) throw new Error('Cannot determine the server room version');
            const policyOptions = {creatorId: currentMatrixUserId, botUserId, roomVersion, invitees: initialInvites};
            const roomOptions: ICreateRoomOpts = {
                room_version: roomVersion,
                ...(options.name ? {name: options.name} : {}),
                topic: options.topic,
                invite: initialInvites,
                preset: options.isDirectMessage ? Preset.TrustedPrivateChat : Preset.PrivateChat,
                is_direct: options.isDirectMessage || false,
                creation_content: {
                    'm.federate': false,
                },
                power_level_content_override: options.isDirectMessage
                    ? createDirectRoomPowerLevels(policyOptions)
                    : createGroupPowerLevels(policyOptions),
                initial_state: [
                    {
                        type: 'm.space.parent',
                        state_key: parentRoomId,
                        content: {
                            via: [window.__ENV__?.SYNAPSE_BASE_URL?.replace('https://', '') || 'matrix.org'],
                            order: Date.now().toString(),
                        }
                    },
                    {
                        type: 'm.room.encryption',
                        state_key: '',
                        content: {
                            algorithm: 'm.megolm.v1.aes-sha2',
                        }
                    },
                    ...(!options.isDirectMessage ? [{
                        type: 'm.room.history_visibility',
                        state_key: '',
                        content: {
                            history_visibility: 'joined',
                        }
                    }] : [])
                ]
            };

            const result = await this.client.createRoom(roomOptions);

            // Invites passed in the createRoom call are sent server-side and are
            // already accepted once createRoom resolves.
            onInviteProgress?.(initialInvites.length, allInvites.length);

            await this.awaitOurMembership(result.room_id);
            const createdRoom = this.client?.getRoom(result.room_id);
            if (!createdRoom?.hasEncryptionStateEvent()) {
                this.roomsPendingEncryption.add(result.room_id);
            }
            if (allInvites.length > 0) {
                this.roomPendingMemberIds.set(result.room_id, new Set(allInvites));
                this.trackInitialInvitees(result.room_id, allInvites);
            }
            if (remainingInvites.length > 0) {
                const inviter = new MultiInviter(
                    this.client!,
                    result.room_id,
                    (completed) => onInviteProgress?.(initialInvites.length + completed, allInvites.length),
                );
                const inviteStates = await inviter.invite(remainingInvites);

                const failedInvites = Object.keys(inviteStates).filter(
                    userId => inviteStates[userId] === 'error'
                );

                if (failedInvites.length > 0) {
                    logger.warn(`[WEB CHAT] Failed to invite ${failedInvites.length} users`, { source: 'chat', event: 'invite_failed', roomId: result.room_id, failedUserIds: failedInvites });
                    failedInvites.forEach(userId => {
                        const error = inviter.getErrorText(userId);
                        logger.error(`[WEB CHAT] Failed to invite user ${userId}: ${error}`, { source: 'chat', event: 'invite_user_error', roomId: result.room_id, targetUserId: userId });
                    });
                    const pending = this.roomPendingMemberIds.get(result.room_id);
                    if (pending) {
                        failedInvites.forEach(userId => pending.delete(userId));
                        if (pending.size === 0) {
                            this.roomPendingMemberIds.delete(result.room_id);
                        }
                    }
                }
            }

            if (options.isDirectMessage && options.invite && options.invite.length > 0) {
                await this.markAsDM(this.client, options.invite[0], result.room_id);
            }

            this.updateRoomsList();

            return result.room_id;
        } catch (error) {
            logger.error(`[WEB CHAT] Failed to create child room: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'create_child_room_error', roomId: parentRoomId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async markAsDM(_client: MatrixClient, otherUserId: string, roomId: string) {
      // Serialised, server-merged write; skipped (never from a local copy) if the server read fails.
      await this.addDirectRoomToAccountData(roomId, otherUserId);
    }

    async createRoom(options: {
        name?: string;
        topic?: string;
        invite?: string[];
        isDirectMessage?: boolean;
    }, onInviteProgress?: (invited: number, total: number) => void): Promise<string> {
        if (!this.client) throw new Error('Client not initialized');

        const parentRoom = this.findParentRoom();

        if (!parentRoom) {
            throw new Error('No parent Space found. Cannot create room without a Space.');
        }

        return this.createRoomAsChild(options, parentRoom.roomId, onInviteProgress);
    }

    async changeRoomName(groupId: string, newName: string): Promise<void> {
        if (!this.client) throw new Error('Matrix client not initialized');
        try {
            await this.client.setRoomName(groupId, newName);
            this.emitStateChange();
        } catch (error) {
            logger.error(`[WEB CHAT] Error changing group name: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'change_room_name_error', roomId: groupId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async changeRoomAvatar(roomId: string, file: File): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');
        try {
            const upload = await this.client.uploadContent(file, {
                name: file.name,
                type: file.type,
            });
            await this.client.sendStateEvent(roomId, EventType.RoomAvatar, {
                url: upload.content_uri,
                info: {
                    mimetype: file.type,
                    size: file.size,
                },
            }, '');
            this.emitStateChange();
        } catch (error) {
            logger.error(`[WEB CHAT] Error changing room avatar: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'change_room_avatar_error', roomId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async kickUserFromRoom(roomId: string, userId: string, reason?: string): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        try {
            await this.kickUserFromRoomOnBackend(roomId, userId, reason);
            await this.reconcileRoomState(roomId);
        } catch (error) {
            logger.error(`[WEB CHAT] Failed to kick user from room: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'kick_user_error', roomId, targetUserId: userId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    private async kickUserFromRoomOnBackend(roomId: string, userId: string, reason?: string): Promise<void> {
        const baseUrl = await getApiUrl() as string;
        const state = store.getState();
        const tenant = state.auth?.tenant;
        const deviceId = state.auth?.deviceId;
        const matrixAccessToken = this.client?.getAccessToken();

        if (!tenant) throw new Error('Missing tenant header');
        if (!matrixAccessToken) throw new Error('Missing Matrix access token');

        const response = await authenticatedFetch(`${baseUrl}/api/v1/chats/rooms/${encodeURIComponent(roomId)}/kick`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Matrix-Authorization': `Bearer ${matrixAccessToken}`,
                'tenant': tenant,
                ...(deviceId ? {'X-Device-Id': deviceId} : {}),
            },
            body: JSON.stringify({
                userId,
                reason,
            }),
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => '');
            throw createRoomKickError(response.status, response.statusText, errorText);
        }

        const body = await response.json().catch(() => null) as { status?: string } | null;
        if (body?.status === 'KICKED_CLEANUP_FAILED') {
            logger.warn('[WEB CHAT] User was kicked from room but backend failed to clean power levels', {
                source: 'chat',
                event: 'kick_user_cleanup_failed',
                roomId,
                targetUserId: userId,
            });
        }
    }

    private async updateAiBotRoom({
        roomId,
        operationPath,
        method,
    }: AiBotRoomRequest): Promise<AiBotRoomOperationResponse> {
        if (!this.client) throw new Error('Client not initialized');

        const baseUrl = await getApiUrl() as string;
        const state = store.getState();
        const tenant = state.auth?.tenant;
        const deviceId = state.auth?.deviceId;
        const matrixAccessToken = this.client.getAccessToken();

        if (!tenant) throw new Error('Missing tenant header');
        if (!matrixAccessToken) throw new Error('Missing Matrix access token');

        const response = await authenticatedFetch(
            `${baseUrl}/api/v1/chats/rooms/${encodeURIComponent(roomId)}/ai-bot/${operationPath}`,
            {
                method,
                headers: {
                    'X-Matrix-Authorization': `Bearer ${matrixAccessToken}`,
                    'tenant': tenant,
                    ...(deviceId ? {'X-Device-Id': deviceId} : {}),
                },
            },
        );
        if (!response.ok) {
            const errorBody = await response.text().catch(() => '');
            throw createAiBotRoomError(response.status, response.statusText, errorBody);
        }

        const result = await response.json() as AiBotRoomOperationResponse;
        const botUserId = operationPath === 'invite' ? getBotUserId(this.client.getUserId() ?? '') : null;
        if (botUserId) {
            this.awaitMemberJoinBeforeNextSend(roomId, botUserId);
        }
        await this.reconcileRoomState(roomId);
        return result;
    }

    async refreshRoomState(roomId: string): Promise<void> {
        const client = this.client;
        if (!client) throw new Error('Matrix client not initialized');
        const room = client.getRoom(roomId);
        if (!room) throw new Error('Room is not available yet; wait for sync and refresh again');
        await room.loadMembersIfNeeded();
        const previousEvents = new Map<string, string | undefined>();
        room.currentState.events.forEach((events, type) => events.forEach((event, stateKey) => {
            previousEvents.set(JSON.stringify([type, stateKey]), event.getId());
        }));
        const state = await client.roomState(roomId);
        if (this.client !== client || client.getRoom(roomId) !== room) return;
        const unchangedEvents = state.filter(event => {
            const stateKey = event.state_key ?? '';
            const currentEvent = room.currentState.getStateEvents(event.type, stateKey);
            return currentEvent?.getId() === previousEvents.get(JSON.stringify([event.type, stateKey]));
        });
        room.currentState.setStateEvents(unchangedEvents.map(event => new MatrixEvent(event)));
        this.emitStateChange();
    }

    private async reconcileRoomState(roomId: string): Promise<void> {
        try {
            await this.refreshRoomState(roomId);
        } catch (error) {
            logger.warn('[WEB CHAT] Room operation succeeded; state refresh will resume through sync', {
                source: 'chat', roomId, error: String(error),
            });
        }
    }

    async enableRoomEncryption(roomId: string): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        const room = this.client.getRoom(roomId);
        if (room?.hasEncryptionStateEvent()) {
            return;
        }

        await this.client.sendStateEvent(
            roomId,
            'm.room.encryption' as const as keyof StateEvents,
            { algorithm: 'm.megolm.v1.aes-sha2' },
            ''
        );
    }

    async isUserJoinedOrInvited(roomId: string, userId: string): Promise<boolean> {
        const membership = await this.getRoomMemberMembership(roomId, userId);
        return membership === 'join' || membership === 'invite';
    }

    private async getRoomMemberMembership(roomId: string, userId: string): Promise<string | null> {
        if (!this.client) throw new Error('Client not initialized');

        const localMembership = this.client.getRoom(roomId)?.getMember(userId)?.membership ?? null;

        try {
            const content = await this.client.getStateEvent(roomId, EventType.RoomMember, userId);
            return typeof content?.membership === 'string' ? content.membership : localMembership;
        } catch (error) {
            const stateError = error as { errcode?: string; httpStatus?: number; data?: { errcode?: string } };
            if (stateError.httpStatus === 404 || stateError.errcode === 'M_NOT_FOUND' || stateError.data?.errcode === 'M_NOT_FOUND') {
                return null;
            }

            logger.warn(`[WEB CHAT] Failed to fetch room member state: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'get_member_state_error', roomId, targetUserId: userId, error: error instanceof Error ? error.message : String(error) });
            return localMembership;
        }
    }

    async inviteUserToRoom(roomId: string, userId: string): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        try {
            const room = this.client.getRoom(roomId);
            if (room && room.hasEncryptionStateEvent() && !this.isDirectMessageRoom(room)) {
                await this.ensureHistoryVisibilityJoined(roomId);
            }

            if (!await this.isUserJoinedOrInvited(roomId, userId)) {
                await this.client.invite(roomId, userId);
            }
            if (room?.hasEncryptionStateEvent() || this.roomsPendingEncryption.has(roomId)) {
                this.awaitMemberJoinBeforeNextSend(roomId, userId);
            }
        } catch (error) {
            logger.error(`[WEB CHAT] Failed to invite user to room: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'invite_user_error', roomId, targetUserId: userId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    private awaitMemberJoinBeforeNextSend(roomId: string, userId: string): void {
        const pending = this.roomPendingMemberIds.get(roomId) ?? new Set<string>();
        pending.add(userId);
        this.roomPendingMemberIds.set(roomId, pending);
    }

    private async ensureHistoryVisibilityJoined(roomId: string): Promise<void> {
        if (!this.client) return;

        try {
            const room = this.client.getRoom(roomId);
            if (!room) return;

            const historyEvent = room.currentState.getStateEvents('m.room.history_visibility', '');
            const currentVisibility = historyEvent?.getContent()?.history_visibility;

            if (currentVisibility === 'joined') return;

            await this.client.sendStateEvent(
                roomId,
                EventType.RoomHistoryVisibility,
                { history_visibility: HistoryVisibility.Joined },
                ''
            );
        } catch (error) {
            logger.warn(`[WEB CHAT] Failed to set history_visibility to joined: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'history_visibility_error', roomId, error: error instanceof Error ? error.message : String(error) });
        }
    }

    getCurrentUserMatrixId(): string {

        return this.client?.getUserId() || '';
    }

    async searchMatrixIdByName(firstName: string, lastName: string): Promise<string | null> {
        if (!this.client) {
            console.warn('Matrix client not initialized');
            return null;
        }

        const searchTerm = `${firstName} ${lastName}`.trim();
        if (!searchTerm) return null;

        try {
            const results = await this.client.searchUserDirectory({ term: searchTerm });
            const match = results.results.find((r: any) => {
                return r.display_name?.toLowerCase() === searchTerm.toLowerCase();
            });

            return match ? match.user_id : null;
        } catch (error) {
            console.error(`Failed to search for user ${searchTerm}:`, error);
            return null;
        }
    }

    /**
     * True when the room was created as a DM.
     *
     * `is_direct` is set on the m.room.member invite event (from the createRoom
     * request) — Synapse does NOT copy it into m.room.create content, so reading
     * it from there always returned false. That silently disabled the m.direct
     * self-heal in useMatrix, which in turn left findDirectMessageRoom unable to
     * locate the existing DM, so every call created a brand-new room.
     */
    wasRoomCreatedAsDirect(roomId: string): boolean {
        if (!this.client) return false;

        const room = this.client.getRoom(roomId);
        if (!room) return false;

        const memberEvents = room.currentState?.getStateEvents('m.room.member') || [];
        for (const memberEvent of memberEvents) {
            if (memberEvent?.getContent()?.is_direct || memberEvent?.getPrevContent()?.is_direct) {
                return true;
            }
        }

        return false;
    }

    private roomMembersMatch(room: Room, targetUserIds: string[]): boolean {
        const currentUserId = this.getCurrentUserMatrixId();
        const expectedMemberIds = [...targetUserIds, currentUserId]
            .filter(id => !!id && !isServiceParticipant(id));
        const expectedMembers = new Set(expectedMemberIds);
        const actualMembers = new Set(
            room.getMembers()
                .filter(member => member.membership === 'join' || member.membership === 'invite')
                .map(member => member.userId)
                .filter(id => !!id && !isServiceParticipant(id))
        );

        if (actualMembers.size !== expectedMembers.size) return false;

        return [...actualMembers].every(userId => expectedMembers.has(userId));
    }

    findDirectMessageRoom(targetUserIds: string[]): string | undefined {
        // Primary: check m.direct account data
        const directData = this.client?.getAccountData(EventType.Direct);
        if (directData) {
            const directRooms = directData.getContent();
            for (const userId in directRooms) {
                if (targetUserIds.includes(userId) || userId === this.getCurrentUserMatrixId()) {
                    const roomIds = [...directRooms[userId]];
                    for (const roomId of roomIds) {
                        const room = this.client?.getRoom(roomId);
                        if (room && this.roomMembersMatch(room, targetUserIds)) {
                            return room.roomId;
                        }
                    }
                }
            }
        }

        // Fallback: scan all joined rooms by membership in case m.direct is stale or missing.
        // Must NOT read is_direct from m.room.create — it is never there (see
        // wasRoomCreatedAsDirect). isDirectMessageRoom checks the m.room.member
        // invite event, which is where createRoom actually records it; using the
        // create event made this whole fallback dead, so a stale m.direct meant a
        // brand-new room per call.
        const allRooms = this.client?.getRooms() ?? [];
        for (const room of allRooms) {
            if (room.getMyMembership() !== 'join') continue;
            if (!this.isDirectMessageRoom(room)) continue;
            if (this.roomMembersMatch(room, targetUserIds)) {
                return room.roomId;
            }
        }

        return undefined;
    }

    findDirectMessageRoomOutcome(targetUserIds: string[]): DirectMessageRoomOutcome {
        const isRoomListFullyLoaded = slidingSyncManager.isRoomListFullyLoaded();
        const directData = this.client?.getAccountData(EventType.Direct);
        let pendingRoomId: string | undefined;

        if (directData) {
            const directRooms = directData.getContent();
            for (const userId in directRooms) {
                const isTargetKey = targetUserIds.includes(userId);
                if (!isTargetKey && userId !== this.getCurrentUserMatrixId()) continue;

                const roomIds = [...directRooms[userId]];
                for (const roomId of roomIds) {
                    const room = this.client?.getRoom(roomId);
                    if (room && this.roomMembersMatch(room, targetUserIds)) {
                        return {status: 'found', roomId: room.roomId};
                    }
                    if (!room && isTargetKey && !isRoomListFullyLoaded && !pendingRoomId) {
                        pendingRoomId = roomId;
                    }
                }
            }
        }

        const allRooms = this.client?.getRooms() ?? [];
        for (const room of allRooms) {
            if (room.getMyMembership() !== 'join') continue;
            if (!this.isDirectMessageRoom(room)) continue;
            if (this.roomMembersMatch(room, targetUserIds)) {
                return {status: 'found', roomId: room.roomId};
            }
        }

        if (pendingRoomId) {
            return {status: 'pending', roomId: pendingRoomId};
        }

        if (!isRoomListFullyLoaded) {
            return {status: 'pending'};
        }

        return {status: 'not-found'};
    }

    async waitForDirectMessageRoom(targetUserIds: string[], timeoutMs: number = 15_000): Promise<DirectMessageRoomOutcome> {
        let outcome = this.findDirectMessageRoomOutcome(targetUserIds);
        const deadline = Date.now() + timeoutMs;

        while (outcome.status === 'pending') {
            const remaining = deadline - Date.now();
            if (remaining <= 0) {
                return outcome;
            }

            await slidingSyncManager.waitForRoomListFullyLoaded(remaining);
            outcome = this.findDirectMessageRoomOutcome(targetUserIds);
        }

        return outcome;
    }

    /**
     * `encrypted` narrows the match to rooms of that encryption state.
     * Encryption cannot be turned off once a room has it, so an encrypted and
     * an unencrypted room with identical members are genuinely different
     * rooms; treating them as one left the user unable to create the encrypted
     * group they asked for, silently offering the unencrypted one instead.
     */
    findUnnamedGroupRoom(targetUserIds: string[], encrypted?: boolean): string | undefined {
        if (!this.client) return undefined;

        const rooms = this.client.getRooms();

        for (const room of rooms) {
            try {
                if (room.getMyMembership() !== 'join') continue;
                if (this.isDirectMessageRoom(room)) continue;
                if (encrypted !== undefined && room.hasEncryptionStateEvent() !== encrypted) continue;

                const nameEvent = room.currentState.getStateEvents('m.room.name', '');
                const roomName = nameEvent?.getContent()?.name;
                if (typeof roomName === 'string' && roomName.trim()) continue;

                if (this.roomMembersMatch(room, targetUserIds)) {
                    return room.roomId;
                }
            } catch (error) {
                logger.error(`[WEB CHAT] Error checking unnamed group room: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'check_unnamed_group_room_error', roomId: room.roomId, error: error instanceof Error ? error.message : String(error) });
                continue;
            }
        }

        return undefined;
    }

    findNamedRoom(roomName: string, targetUserIds: string[], encrypted?: boolean): string | null {
        if (!this.client || !roomName) return null;

        const rooms = this.client.getRooms();

        for (const room of rooms) {
            try {
                if (room.getMyMembership() !== 'join') continue;
                // Same reasoning as findUnnamedGroupRoom: encryption is part of
                // a room's identity, not a setting that can be changed later.
                if (encrypted !== undefined && room.hasEncryptionStateEvent() !== encrypted) continue;

                const roomState = room.currentState;
                const nameEvent = roomState.getStateEvents('m.room.name', '');
                const currentRoomName = nameEvent?.getContent()?.name;

                if (currentRoomName !== roomName) continue;

                if (this.roomMembersMatch(room, targetUserIds)) {
                    return room.roomId;
                }
            } catch (error) {
                logger.error(`[WEB CHAT] Error checking named room: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'check_named_room_error', roomId: room.roomId, error: error instanceof Error ? error.message : String(error) });
                continue;
            }
        }

        return null;
    }

    async sendFile(roomId: string, file: File, replyToEventId?: string, caption?: string, batchId?: string, mentions?: Array<{
        userId: string,
        displayName: string
    }>, privateReplySource?: PrivateReplySource): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        const room = this.client.getRoom(roomId);
        if (!room) {
            this.updateRoomsList();
            throw new Error('Room not found');
        }

        await this.ensureRoomEncryptionReady(roomId);
        await this.ensureRoomMembersReady(roomId);

        const isEncrypted = room?.hasEncryptionStateEvent();
        if (isEncrypted) {
            const crypto = this.client.getCrypto();
            if (crypto) {
                const members = room.getJoinedMembers();
                const userIds = members.map(m => m.userId);
                if (userIds.length > 0) {
                    logger.info(`[WEB CHAT] sendFile: downloading device keys for ${userIds.length} member(s)`, this.encCtx({
                        event: 'send_file_device_keys',
                        roomId,
                        memberCount: userIds.length,
                    }));
                    await crypto.getUserDeviceInfo(userIds, true);
                }
            }
        }

        try {
            const isImage = file.type.startsWith('image/');
            const isVideo = file.type.startsWith('video/');
            const [upload, dimensions, thumbnailFields, videoFields] = await Promise.all([
                this.uploadMediaForRoom(file, isEncrypted),
                isImage ? this.getImageDimensions(file) : Promise.resolve(undefined),
                isImage ? this.buildImageThumbnailFields(file, isEncrypted, roomId) : Promise.resolve(undefined),
                isVideo ? this.buildVideoInfoFields(file, isEncrypted, roomId) : Promise.resolve(undefined),
            ]);

            const msgtype = this.getMessageTypeForFile(file);
            const trimmedCaption = caption?.trim();
            const formattedCaption = trimmedCaption && mentions?.length
                ? formatMediaCaptionWithMentions(trimmedCaption, mentions)
                : null;
            const mediaInfo: ChatMediaInfo = {
                size: file.size,
                mimetype: file.type,
                ...(dimensions ? { w: dimensions.width, h: dimensions.height } : {}),
                ...(thumbnailFields ?? {}),
                ...(videoFields ?? {}),
            };

            const quote = privateReplySource ? buildPrivateReplyQuote(privateReplySource) : null;
            const body = quote
                ? `${quote.body}${trimmedCaption ? `\n\n${trimmedCaption}` : ''}`
                : (trimmedCaption || file.name);
            const captionHtml = formattedCaption?.formattedBody
                ?? (trimmedCaption ? escapeHtml(trimmedCaption) : '');

            const content = {
                msgtype,
                body,
                filename: file.name,
                info: mediaInfo,
                ...(upload.file ? { file: upload.file } : { url: upload.url }),
                ...(!quote && replyToEventId
                    ? {
                        'm.relates_to': {
                            'm.in_reply_to': {
                                event_id: replyToEventId,
                            },
                        },
                    }
                    : {}),
                ...(batchId ? { 'org.phonehq.batch_id': batchId } : {}),
                ...(quote
                    ? {
                        format: 'org.matrix.custom.html',
                        formatted_body: `${quote.formattedBody}${captionHtml}`,
                        ...(formattedCaption ? {'m.mentions': {user_ids: formattedCaption.userIds}} : {}),
                    }
                    : formattedCaption
                        ? {
                            format: 'org.matrix.custom.html',
                            formatted_body: formattedCaption.formattedBody,
                            'm.mentions': {
                                user_ids: formattedCaption.userIds,
                            },
                        }
                        : {}),
            } as RoomMessageEventContent;

            await this.client.sendMessage(roomId, content);
            this.roomPendingMemberIds.delete(roomId);

        } catch (error) {
            logger.error(`[WEB CHAT] sendFile failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'send_file_error',
                roomId,
                isEncrypted,
                fileName: file.name,
                fileType: file.type,
                error: error instanceof Error ? error.message : String(error),
            }));
            throw error;
        }
    }

    private getMessageTypeForFile(file: File): MsgType {
        if (file.type.startsWith('image/')) return MsgType.Image;
        if (file.type.startsWith('video/')) return MsgType.Video;
        if (file.type.startsWith('audio/')) return MsgType.Audio;
        return MsgType.File;
    }

    private async uploadMediaForRoom(
        file: File,
        isEncrypted: boolean | undefined,
        abortController?: AbortController
    ): Promise<{ url?: string; file?: MatrixEncryptedFile }> {
        if (!this.client) throw new Error('Client not initialized');

        if (!isEncrypted) {
            const upload = await this.client.uploadContent(file, {
                name: file.name,
                type: file.type,
                ...(abortController ? { abortController } : {}),
            });
            return { url: upload.content_uri };
        }

        const encryptResult = await encryptMatrixAttachment(await file.arrayBuffer());
        const encryptedBlob = new Blob([encryptResult.data], { type: 'application/octet-stream' });
        const uploadOptions: UploadOpts = {
            includeFilename: false,
            type: 'application/octet-stream',
            ...(abortController ? { abortController } : {}),
        };
        const upload = await this.client.uploadContent(encryptedBlob, uploadOptions);

        return {
            file: {
                ...encryptResult.info,
                url: upload.content_uri,
            },
        };
    }

    private async uploadThumbnailFields(
        thumbnail: { blob: Blob; width: number; height: number },
        isEncrypted: boolean | undefined,
        abortController?: AbortController,
    ): Promise<Pick<ChatMediaInfo, 'thumbnail_url' | 'thumbnail_file' | 'thumbnail_info'>> {
        const thumbnailFile = new File([thumbnail.blob], 'thumbnail.jpg', { type: THUMBNAIL_MIMETYPE });
        const upload = await this.uploadMediaForRoom(thumbnailFile, isEncrypted, abortController);

        return {
            ...(upload.file ? { thumbnail_file: upload.file } : { thumbnail_url: upload.url }),
            thumbnail_info: {
                w: thumbnail.width,
                h: thumbnail.height,
                mimetype: THUMBNAIL_MIMETYPE,
                size: thumbnail.blob.size,
            },
        };
    }

    private async buildImageThumbnailFieldsUnbounded(
        file: File,
        isEncrypted: boolean | undefined,
    ): Promise<Pick<ChatMediaInfo, 'thumbnail_url' | 'thumbnail_file' | 'thumbnail_info'> | undefined> {
        const thumbnail = await generateImageThumbnail(file);
        if (!thumbnail) return undefined;

        return this.uploadThumbnailFields(thumbnail, isEncrypted);
    }

    private async uploadVideoThumbnailBounded(
        thumbnail: VideoThumbnail,
        isEncrypted: boolean | undefined,
    ): Promise<Pick<ChatMediaInfo, 'thumbnail_url' | 'thumbnail_file' | 'thumbnail_info'>> {
        const abortController = new AbortController();
        const timer = setTimeout(() => abortController.abort(), THUMBNAIL_BUILD_TIMEOUT_MS);
        try {
            return await this.uploadThumbnailFields(thumbnail, isEncrypted, abortController);
        } finally {
            clearTimeout(timer);
        }
    }

    private async generateVideoThumbnailBounded(file: File): Promise<VideoThumbnail | null> {
        const abortController = new AbortController();
        const timer = setTimeout(() => abortController.abort(), THUMBNAIL_BUILD_TIMEOUT_MS);
        try {
            return await generateVideoThumbnail(file, abortController.signal);
        } finally {
            clearTimeout(timer);
        }
    }

    private async buildVideoInfoFields(
        file: File,
        isEncrypted: boolean | undefined,
        roomId: string,
    ): Promise<Pick<ChatMediaInfo, 'w' | 'h' | 'duration' | 'thumbnail_url' | 'thumbnail_file' | 'thumbnail_info'> | undefined> {
        const warn = (stage: string, error: unknown) => {
            logger.warn(`[WEB CHAT] sendFile: video thumbnail ${stage} failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'send_file_thumbnail_failed',
                roomId,
                error: error instanceof Error ? error.message : String(error),
            }));
        };

        let thumbnail: VideoThumbnail | null;
        try {
            thumbnail = await this.generateVideoThumbnailBounded(file);
        } catch (error) {
            warn('extraction', error);
            return undefined;
        }
        if (!thumbnail) return undefined;

        const metadata = {
            w: thumbnail.videoWidth,
            h: thumbnail.videoHeight,
            ...(thumbnail.durationMs > 0 ? { duration: thumbnail.durationMs } : {}),
        };

        try {
            return { ...metadata, ...(await this.uploadVideoThumbnailBounded(thumbnail, isEncrypted)) };
        } catch (error) {
            warn('upload', error);
            return metadata;
        }
    }

    private async buildImageThumbnailFields(
        file: File,
        isEncrypted: boolean | undefined,
        roomId: string,
    ): Promise<Pick<ChatMediaInfo, 'thumbnail_url' | 'thumbnail_file' | 'thumbnail_info'> | undefined> {
        try {
            return await Promise.race([
                this.buildImageThumbnailFieldsUnbounded(file, isEncrypted),
                new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), THUMBNAIL_BUILD_TIMEOUT_MS)),
            ]);
        } catch (error) {
            logger.warn(`[WEB CHAT] sendFile: image thumbnail failed: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({
                event: 'send_file_thumbnail_failed',
                roomId,
                error: error instanceof Error ? error.message : String(error),
            }));
            return undefined;
        }
    }

    private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Failed to read image dimensions'));
            };
            img.src = objectUrl;
        });
    }

    private saveSelectedRoom(roomId: string | null): void {
        if (roomId) {
            localStorage.setItem(MatrixService.SELECTED_ROOM_STORAGE_KEY, roomId);
        } else {
            localStorage.removeItem(MatrixService.SELECTED_ROOM_STORAGE_KEY);
        }
    }

    private getStoredSelectedRoom(): string | null {
        return localStorage.getItem(MatrixService.SELECTED_ROOM_STORAGE_KEY);
    }

    markExplicitRoomSelectionPending(): void {
        this.explicitRoomSelected = true;
    }

    isExplicitRoomSelectionPending(): boolean {
        return this.explicitRoomSelected;
    }

    private startRoomPreviewPrefetch(prefetchClient: MatrixClient): void {
        const isCurrentClient = () => this.client === prefetchClient;
        const prefetcher = new RoomPreviewPrefetcher({
            getRooms: () => prefetchClient.getRooms(),
            hasPreview: (room) => room.getLiveTimeline().getEvents().some(isRelevantTimelineEvent),
            canPaginate: (room) => room.getLiveTimeline().getPaginationToken(Direction.Backward) !== null,
            wasOpened: (roomId) => this.openedRoomIds.has(roomId),
            paginate: (roomId, limit) => this.loadOlderMessages(roomId, limit),
            isActive: isCurrentClient,
            onRoomPaginated: () => {
                if (this.previewPrefetchRefreshTimeout !== null) return;
                this.previewPrefetchRefreshTimeout = window.setTimeout(() => {
                    this.previewPrefetchRefreshTimeout = null;
                    if (!isCurrentClient()) return;
                    this.emitStateChange();
                }, 250);
            },
        });
        void prefetcher.run();
    }

    async selectRoom(roomId: string | null) {
        this.explicitRoomSelected = true;
        const seq = ++this.roomSelectionSeq;
        if (!roomId) {
            this.saveSelectedRoom(null);
            this.chatState.selectedRoomId = null;
            this.chatState.error = null;
            this.emitStateChange();
            return;
        }

        const selectionClient = this.client;
        if (!selectionClient) return;
        this.openedRoomIds.add(roomId);

        const room = this.getRoom(roomId);
        const isEncrypted = room?.hasEncryptionStateEvent() ?? false;

        if (isEncrypted && !this.chatState.hasSecretStorage) {
            logger.info('[WEB CHAT] Waiting for crypto to be ready for encrypted room', this.encCtx({ event: 'select_room_crypto_wait', roomId }));
            const startTime = Date.now();
            while (
                this.client === selectionClient &&
                !this.chatState.hasSecretStorage &&
                Date.now() - startTime < 3000
            ) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        if (this.client !== selectionClient || seq !== this.roomSelectionSeq) return;

        this.saveSelectedRoom(roomId);
        this.chatState.selectedRoomId = roomId;
        this.chatState.error = null;
        this.emitStateChange();

        if (this.chatState.useSlidingSync) {
            logger.info(`[WEB CHAT] Starting room subscription for ${roomId.slice(-6)}`, { source: 'chat', event: 'room_subscription_start', roomId });
            slidingSyncManager.setRoomVisible(roomId).catch(err => {
                logger.warn(`[WEB CHAT] Failed to set room visible: ${err instanceof Error ? err.message : String(err)}`, { source: 'chat', event: 'room_visible_error', roomId, error: err instanceof Error ? err.message : String(err) });
            });
        }

        if (isEncrypted) {
            this.prepareCryptoForRoom(roomId).catch(err => {
                logger.warn(`[WEB CHAT] Failed to prepare crypto for room: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'prepare_crypto_error', roomId, error: err instanceof Error ? err.message : String(err) }));
            });
        }
    }

    private async prepareCryptoForRoom(roomId: string): Promise<void> {
        const crypto = this.client?.getCrypto();
        if (!crypto) return;

        try {
            await this.checkKeyBackupAndEnableWithLogging(crypto, {
                reason: 'room_prepare',
                roomId,
            });
        } catch (err) {
            logger.warn(`[WEB CHAT] Failed to enable key backup: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'key_backup_enable_error', roomId, error: err instanceof Error ? err.message : String(err) }));
        }

        if (!this.downloadedKeys.has(roomId)) {
            try {
                await this.downloadDeviceKeysForRoom(roomId);
            } catch (err) {
                logger.warn(`[WEB CHAT] Failed to download device keys for room: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'download_device_keys_error', roomId, error: err instanceof Error ? err.message : String(err) }));
            }
        }

        const room = this.client?.getRoom(roomId);
        if (room) {
            try {
                // Full decrypt once on room entry so E2E timeline does not drop
                // middle messages that still have undefined body after refresh.
                await room.decryptAllEvents();
                this.emitStateChange();
            } catch (err) {
                logger.warn(`[WEB CHAT] Failed to decrypt room events: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'decrypt_room_events_error', roomId, error: err instanceof Error ? err.message : String(err) }));
            }
        }
    }


    private async restoreSelectedRoom(): Promise<void> {
        const restoreClient = this.client;
        if (!restoreClient) return;
        if (this.explicitRoomSelected) return;

        const storedRoomId = this.getStoredSelectedRoom();
        if (!storedRoomId) return;

        const room = restoreClient.getRoom(storedRoomId);
        if (!room || room.isSpaceRoom()) {
            localStorage.removeItem(MatrixService.SELECTED_ROOM_STORAGE_KEY);
            return;
        }

        const isEncrypted = room?.hasEncryptionStateEvent() ?? false;

        // If room is encrypted, wait for crypto to be ready
        if (isEncrypted && !this.chatState.hasSecretStorage) {
            logger.info('[WEB CHAT] Waiting for crypto to be ready before restoring encrypted room', this.encCtx({ event: 'restore_room_crypto_wait' }));

            // Wait up to 5 seconds for crypto to be ready
            const startTime = Date.now();
            while (
                this.client === restoreClient &&
                !this.chatState.hasSecretStorage &&
                Date.now() - startTime < 5000
            ) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            if (this.client !== restoreClient) return;

            if (!this.chatState.hasSecretStorage) {
                logger.warn('[WEB CHAT] Crypto not ready after 5s, restoring room anyway', this.encCtx({ event: 'restore_room_crypto_timeout' }));
            } else {
                logger.info('[WEB CHAT] Crypto ready, proceeding with room restore', this.encCtx({ event: 'restore_room_crypto_ready' }));
            }
        }

        if (this.client !== restoreClient) return;

        try {
            await this.selectRoom(storedRoomId);
        } catch (err) {
            logger.error(`[WEB CHAT] Failed to restore room: ${err instanceof Error ? err.message : String(err)}`, { source: 'chat', event: 'restore_room_error', roomId: storedRoomId, error: err instanceof Error ? err.message : String(err) });
        }
    }

    getRooms(): Room[] {
        return this.client?.getRooms() || [];
    }

    getRoom(roomId: string | undefined): Room | null {
        if (!roomId) return null;
        return this.client?.getRoom(roomId) || null;
    }

    /**
     * Turns whatever a matrix.to room link carries — a room id or a room alias —
     * into a room id this client can select. Aliases are matched against joined
     * rooms first so the common case costs nothing; only an alias for a room we
     * do not hold locally falls through to the homeserver.
     */
    async resolveRoomId(roomIdOrAlias: string): Promise<string | null> {
        if (!roomIdOrAlias) return null;
        if (roomIdOrAlias.startsWith('!')) return roomIdOrAlias;
        if (!roomIdOrAlias.startsWith('#')) return null;

        const localMatch = this.getRooms().find(room =>
            room.getCanonicalAlias() === roomIdOrAlias ||
            room.getAltAliases().includes(roomIdOrAlias)
        );
        if (localMatch) return localMatch.roomId;

        try {
            const {room_id: roomId} = await this.client?.getRoomIdForAlias(roomIdOrAlias) ?? {};
            return roomId ?? null;
        } catch (error) {
            logger.warn('[WEB CHAT] Could not resolve room alias', {
                source: 'chat',
                event: 'room_alias_resolve_failed',
                alias: roomIdOrAlias,
                error: error instanceof Error ? error.message : String(error),
            });
            return null;
        }
    }

    async getCryptoRuntimeStatus(): Promise<CryptoRuntimeStatus> {
        const client = this.client;
        const crypto = client?.getCrypto();
        if (!client || !crypto) throw new Error('Crypto is unavailable');

        const [backupInfo, activeVersion, privateKey, crossSigningReady, secretStorageReady, secretStorageKey] = await Promise.all([
            crypto.getKeyBackupInfo(),
            crypto.getActiveSessionBackupVersion(),
            crypto.getSessionBackupPrivateKey(),
            crypto.isCrossSigningReady(),
            crypto.isSecretStorageReady(),
            client.secretStorage.getKey(),
        ]);
        const trustInfo = backupInfo
            ? await crypto.isKeyBackupTrusted(backupInfo)
            : {trusted: false, matchesDecryptionKey: false};

        const secretStorageKeyId = secretStorageKey?.[0] ?? null;
        const secretStorageKeyInfo = secretStorageKey?.[1];
        const passphraseInfo = secretStorageKeyInfo?.passphrase;
        const secretStoragePbkdf2Configured = passphraseInfo?.algorithm === 'm.pbkdf2'
            && typeof passphraseInfo.salt === 'string'
            && passphraseInfo.salt.length > 0
            && Number.isSafeInteger(passphraseInfo.iterations)
            && passphraseInfo.iterations > 0;
        const cachedKey = this.cachedSecretStorageKey;
        let secretStorageCachedKeyMatches: boolean | null = null;
        if (cachedKey && secretStorageKeyId === cachedKey.keyId && secretStorageKeyInfo) {
            secretStorageCachedKeyMatches = await client.secretStorage.checkKey(cachedKey.key, secretStorageKeyInfo);
        }

        const serverVersion = backupInfo ? String(backupInfo.version) : null;
        const privateKeyPresent = Boolean(privateKey);
        return {
            serverVersion,
            activeVersion,
            privateKeyPresent,
            trustedSignature: trustInfo.trusted,
            matchesDecryptionKey: trustInfo.matchesDecryptionKey,
            usable: Boolean(
                serverVersion
                && activeVersion === serverVersion
                && privateKeyPresent
                && trustInfo.matchesDecryptionKey
            ),
            serverSessionCount: typeof backupInfo?.count === 'number' ? backupInfo.count : null,
            crossSigningReady,
            secretStorageReady,
            secretStoragePbkdf2Configured,
            secretStoragePbkdf2Iterations: secretStoragePbkdf2Configured
                ? passphraseInfo.iterations
                : null,
            secretStorageKeyCached: Boolean(cachedKey),
            secretStorageCachedKeyMatches,
            backupStatus: this.chatState.backupStatus,
        };
    }

    async revalidateAccessToken(): Promise<string | null> {
        const session = this.currentTokenSession();
        if (!session) return null;

        try {
            if (!this.tokenRefreshInFlight && !this.isTokenRefreshDue() && await this.isAccessTokenAccepted(session)) {
                return this.isTokenSessionCurrent(session) ? session.client.getAccessToken() : null;
            }
            const refreshed = await this.refreshTokens(session);
            return refreshed.accessToken;
        } catch (error) {
            if (!this.isTokenSessionCurrent(session)) return null;
            throw error;
        }
    }

    private async isAccessTokenAccepted(session: MatrixTokenSession): Promise<boolean> {
        const accessToken = session.client.getAccessToken();
        if (!accessToken) return false;

        const response = await this.requestWhoami(accessToken, session.signal);
        if (response.status === 401) return false;
        if (!response.ok) {
            throw new Error(`Failed to validate Matrix access token: ${response.status}`);
        }
        return true;
    }

    getClient(): MatrixClient | null {
        return this.client;
    }

    getState(): ChatState {
        return {...this.chatState};
    }

    isRoomInSetup(roomId: string): boolean {
        return this.roomsInSetup.has(roomId);
    }

    private trackInitialInvitees(roomId: string, userIds: string[]): void {
        this.roomInitialInviteeIds.set(roomId, new Set(userIds));
        // Bound memory: drop the cohort after invitees have had time to join.
        setTimeout(() => this.roomInitialInviteeIds.delete(roomId), 10 * 60 * 1000);
    }

    // Returns true if userId was invited at room creation, removing it from the
    // cohort so subsequent (re)joins are treated normally.
    consumeInitialInvitee(roomId: string, userId: string): boolean {
        const invitees = this.roomInitialInviteeIds.get(roomId);
        if (!invitees || !invitees.has(userId)) return false;

        invitees.delete(userId);
        if (invitees.size === 0) {
            this.roomInitialInviteeIds.delete(roomId);
        }
        return true;
    }

    /**
     * Load more rooms when user scrolls (for sliding sync)
     * @param additionalCount Number of additional rooms to load
     */
    async loadMoreRooms(additionalCount: number = 50): Promise<void> {
        if (this.chatState.useSlidingSync) {
            await slidingSyncManager.loadMoreRooms('all_rooms', additionalCount);
        }
    }

    /**
     * Get total room count (for sliding sync pagination)
     */
    getTotalRoomCount(): number {
        if (this.chatState.useSlidingSync) {
            return slidingSyncManager.getRoomCount();
        }
        return this.getRooms().length;
    }

    private isListedMessage(event: MatrixEvent): boolean {
        const content = event.getContent();
        // Hide replacement events (edits) from showing as separate messages
        if (content?.['m.relates_to']?.rel_type === 'm.replace') return false;
        // Hide custom phonehq events from timeline
        if (content?.msgtype?.startsWith('org.phonehq.transcription_') || content?.msgtype?.startsWith('org.phonehq.summary_') || isTranscriptionMessageContent(content) || isSummaryMessageContent(content)) return false;
        if (content?.body || content?.msgtype) return true;
        const original = event.getOriginalContent();
        if (!original?.body && !original?.msgtype) return false;
        this.decryptPendingReplacement(event);
        return true;
    }

    private decryptPendingReplacement(event: MatrixEvent): void {
        const replacement = event.replacingEvent();
        const client = this.client;
        if (!replacement || !client || !replacement.shouldAttemptDecryption()) return;
        replacement.event.room_id ??= event.getRoomId();
        void client.decryptEventIfNeeded(replacement);
    }

    getRoomMessages(roomId: string, limit: number = 50): MatrixEvent[] {
        const room = this.getRoom(roomId);
        if (!room) {
            logger.warn('[WEB CHAT] getRoomMessages: Room not found', { source: 'chat', event: 'room_not_found', roomId });
            return [];
        }

        try {
            const timeline = room.getLiveTimeline();
            if (!timeline) {
                return [];
            }

            const events = timeline.getEvents();
            const messageEvents = events.filter(event => {
                if (event.isRedacted()) return true;

                const eventType = event.getType();
                const isMessage = eventType === 'm.room.message' || eventType === EventType.RoomMessage;

                if (isMessage) return this.isListedMessage(event);

                // A call belongs to the conversation the way a message does.
                // The invite anchors its row; answer/candidates/hangup stay
                // invisible, so a call draws one bubble and not five.
                if (isCallInviteEvent(event) || isRtcNotificationEvent(event)) return true;

                const isSystemEvent = eventType === 'm.room.member' ||
                    eventType === 'm.room.name' ||
                    eventType === 'm.room.topic';

                return isSystemEvent;
            });

            let firstMessageIndex = -1;
            for (let i = 0; i < messageEvents.length; i++) {
                const eventType = messageEvents[i].getType();
                const isRealMessage = eventType === 'm.room.message' || eventType === EventType.RoomMessage
                    || isCallInviteEvent(messageEvents[i])
                    || isRtcNotificationEvent(messageEvents[i]);
                if (isRealMessage) {
                    firstMessageIndex = i;
                    break;
                }
            }

            if (firstMessageIndex === -1) {
                const onlyMessageEvents = messageEvents.filter(event => {
                    const eventType = event.getType();
                    return eventType === 'm.room.message' || eventType === EventType.RoomMessage
                        || isCallInviteEvent(event)
                        || isRtcNotificationEvent(event);
                });
                const limitedEvents = onlyMessageEvents.slice(-limit);
                this.logRoomEventsDump(roomId, events, limitedEvents);
                return limitedEvents;
            }

            const filteredEvents = messageEvents.filter((event, index) => {
                const eventType = event.getType();
                const isSystemEvent = eventType === 'm.room.member' ||
                    eventType === 'm.room.name' ||
                    eventType === 'm.room.topic';

                if (!isSystemEvent) return true;

                return index >= firstMessageIndex;
            });
            const limitedEvents = filteredEvents.slice(-limit);
            this.logRoomEventsDump(roomId, events, limitedEvents);
            return limitedEvents;
        } catch (error) {
            logger.error(`[WEB CHAT] getRoomMessages error: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'get_messages_error', roomId, error: error instanceof Error ? error.message : String(error) });
            return [];
        }
    }

    async loadOlderMessages(roomId: string, count: number = 20): Promise<{ messages: MatrixEvent[]; hasMore: boolean }> {
        const room = this.getRoom(roomId);
        if (!room) {
            return { messages: [], hasMore: false };
        }

        try {
            const timeline = room.getLiveTimeline();
            if (!timeline) return { messages: [], hasMore: false };
            const eventCountBeforePaginate = timeline.getEvents().length;

            // Use sliding sync manager for backpagination if enabled
            if (this.chatState.useSlidingSync) {
                await slidingSyncManager.loadMoreMessages(roomId, count);
            } else {
                await this.client?.scrollback(room, count);
            }

            if (room.hasEncryptionStateEvent()) {
                try {
                    const eventsAfterPaginate = timeline.getEvents();
                    const newlyLoadedCount = Math.max(0, eventsAfterPaginate.length - eventCountBeforePaginate);
                    const client = this.client;
                    if (newlyLoadedCount > 0 && client) {
                        const newlyLoadedEvents = eventsAfterPaginate.slice(0, newlyLoadedCount);
                        const encryptedEvents = newlyLoadedEvents.filter(event =>
                            event.getType() === 'm.room.encrypted' ||
                            (typeof event.isDecryptionFailure === 'function' && event.isDecryptionFailure())
                        );

                        if (encryptedEvents.length > 0) {
                            await Promise.allSettled(
                                encryptedEvents.map(event => client.decryptEventIfNeeded(event))
                            );
                        }
                    }
                } catch (err) {
                    logger.warn(`[WEB CHAT] Failed to decrypt paginated events: ${err instanceof Error ? err.message : String(err)}`, this.encCtx({ event: 'decrypt_paginated_events_error', roomId, error: err instanceof Error ? err.message : String(err) }));
                }
            }

            const events = timeline.getEvents();

            const messageEvents = events.filter(event => {
                if (event.isRedacted()) return true;

                const eventType = event.getType();
                const isMessage = eventType === 'm.room.message' || eventType === EventType.RoomMessage;

                if (isMessage) return this.isListedMessage(event);

                // A call belongs to the conversation the way a message does.
                // The invite anchors its row; answer/candidates/hangup stay
                // invisible, so a call draws one bubble and not five.
                if (isCallInviteEvent(event) || isRtcNotificationEvent(event)) return true;

                const isSystemEvent = eventType === 'm.room.member' ||
                    eventType === 'm.room.name' ||
                    eventType === 'm.room.topic';

                return isSystemEvent;
            });

            const hasMore = !!timeline.getPaginationToken(Direction.Backward);

            return { messages: messageEvents, hasMore };
        } catch (error) {
            logger.error(`[WEB CHAT] loadOlderMessages error: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'load_older_messages_error', roomId, error: error instanceof Error ? error.message : String(error) });
            return { messages: [], hasMore: false };
        }
    }

    /**
     * Mute/unmute notifications for a room
     */
    async setRoomMuted(roomId: string, isMuted: boolean): Promise<void> {
        if (!this.client) {
            throw new Error('Matrix client not initialized');
        }

        try {
            if (isMuted) {
                // Delete any existing room-specific rule first
                const roomRule = this.client.getRoomPushRule('global', roomId);
                if (roomRule) {
                    await this.client.deletePushRule('global', PushRuleKind.RoomSpecific, roomRule.rule_id);
                }

                // Add an Override push rule to mute all notifications (including mentions)
                // Override rules have higher priority than room rules and mention rules
                await this.client.addPushRule('global', PushRuleKind.Override, roomId, {
                    conditions: [
                        {
                            kind: ConditionKind.EventMatch,
                            key: 'room_id',
                            pattern: roomId,
                        },
                    ],
                    actions: [PushRuleActionName.DontNotify],
                });
            } else {
                // Timed mutes only exist in the backend, so refresh the Matrix
                // rules before deleting to avoid M_NOT_FOUND for absent rules.
                const pushRules = await this.client.getPushRules();
                const hasOverrideRule = pushRules.global.override?.some((rule) => rule.rule_id === roomId) ?? false;
                const hasRoomRule = pushRules.global.room?.some((rule) => rule.rule_id === roomId) ?? false;

                if (hasOverrideRule) {
                    await this.client.deletePushRule('global', PushRuleKind.Override, roomId);
                }

                if (hasRoomRule) {
                    await this.client.deletePushRule('global', PushRuleKind.RoomSpecific, roomId);
                }

                // addPushRule/deletePushRule do not update MatrixClient.pushRules.
                if (hasOverrideRule || hasRoomRule) {
                    await this.client.getPushRules();
                }
            }
        } catch (error) {
            logger.error(`[WEB CHAT] setRoomMuted error: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'set_muted_error', roomId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    isRoomMuted(roomId: string): boolean {
        if (!this.client) return false;

        try {
            const pushRules = this.client.pushRules;

            const overrideRule = pushRules?.global?.override?.find((rule: any) => {
                if (!rule.enabled || rule.rule_id !== roomId) return false;

                const hasRoomCondition = rule.conditions?.some((cond: any) =>
                    cond.kind === ConditionKind.EventMatch &&
                    cond.key === 'room_id' &&
                    cond.pattern === roomId
                );

                const hasDontNotify = rule.actions?.length === 0 ||
                    rule.actions?.some((action: any) => action === 'dont_notify' || action === PushRuleActionName.DontNotify);

                return hasRoomCondition && hasDontNotify;
            });

            if (overrideRule) {
                return true;
            }

            return false;
        } catch (error) {
            logger.error(`[WEB CHAT] isRoomMuted error: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'is_muted_error', roomId, error: error instanceof Error ? error.message : String(error) });
            return false;
        }
    }

    async leaveRoom(roomId: string, options?: {forget?: boolean}): Promise<void> {
        if (!this.client) {
            throw new Error('Matrix client not initialized');
        }

        const room = this.client.getRoom(roomId);
        if (room?.getType() === 'm.space') {
            throw new Error('Cannot leave a space');
        }

        try {
            await this.client.leave(roomId);
        } catch (error) {
            logger.error(`[WEB CHAT] leaveRoom error: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'leave_room_error', roomId, error: error instanceof Error ? error.message : String(error) });
            throw error;
        }

        if (options?.forget) {
            await this.forgetRoom(roomId);
        }
    }

    async forgetRoom(roomId: string): Promise<boolean> {
        if (!this.client) return false;

        try {
            await this.client.forget(roomId);
            return true;
        } catch (error) {
            logger.warn(`[WEB CHAT] forgetRoom error: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'forget_room_error', roomId, error: error instanceof Error ? error.message : String(error) });
            return false;
        }
    }

    async searchMessageBodies(
        options: SearchOptions
    ): Promise<SearchResponse> {
        const {
            query,
            roomIds,
            limit = 50,
            orderBy = SearchOrderBy.Recent,
            nextBatch,
            includeState = false,
            eventTypes = ['m.room.message'],
            senders,
            beforeLimit = 5,
            afterLimit = 5
        } = options;

        if (!query || query.trim() === '') {
            throw new Error('Search query is required');
        }

        if (query.trim().length < 2) {
            throw new Error('Search query must be at least 2 characters long');
        }

        const searchBody: ISearchRequestBody = {
            search_categories: {
                room_events: {
                    search_term: query.trim(),
                    keys: ['content.body'],
                    filter: {
                        limit: Math.min(limit, 100),
                        types: eventTypes,
                        ...(roomIds && {
                            rooms: Array.isArray(roomIds) ? roomIds : [roomIds]
                        }),
                        ...(senders && {senders: senders})
                    },
                    order_by: orderBy,
                    event_context: {
                        before_limit: Math.min(beforeLimit, 10),
                        after_limit: Math.min(afterLimit, 10),
                        include_profile: true
                    },
                    include_state: includeState,
                    groupings: {
                        group_by: [{
                            key: GroupKey.RoomId,
                        }]
                    }
                }
            }
        };

        const searchParams = {
            body: searchBody,
            ...(nextBatch && {next_batch: nextBatch})
        };

        try {
            if (!this.client) throw new Error('Client not initialized');
            const response: ISearchResponse = await this.client.search(searchParams);

            return {
                results: formatSearchResults(response.search_categories?.room_events?.results || []),
                count: response.search_categories?.room_events?.count || 0,
                nextBatch: response.search_categories?.room_events?.next_batch,
                highlights: response.search_categories?.room_events?.highlights || [],
                groups: response.search_categories?.room_events?.groups || {}
            };

        } catch (error: any) {
            logger.error(`[WEB CHAT] Search error: ${error?.message || String(error)}`, { source: 'chat', event: 'search_error', httpStatus: error?.httpStatus, error: error?.message || String(error) });

            if (error.httpStatus === 400) {
                const errorDetails = error.data?.error || error.message || 'Unknown validation error';
                logger.error(`[WEB CHAT] Search 400 error: ${errorDetails}`, { source: 'chat', event: 'search_validation_error', errorDetails });
                throw new Error(`Invalid search parameters: ${errorDetails}`);
            } else if (error.httpStatus === 429) {
                throw new Error('Search rate limit exceeded. Please try again later.');
            } else if (error.httpStatus === 404) {
                throw new Error('Search not supported by this Matrix server');
            } else if (error.httpStatus === 403) {
                throw new Error('Not authorized to search in this room');
            }

            throw new Error(`Search failed: ${error.message || 'Unknown error'}`);
        }
    }


    refreshRooms() {
        this.updateRoomsList();
    }

    debugRoom(roomId: string) {
        const room = this.getRoom(roomId);
        if (!room) {
            return;
        }
    }

    async waitForRoom(roomId: string, timeout: number = 3000): Promise<Room | null> {
        return new Promise((resolve) => {
            const checkRoom = () => {
                const room = this.getRoom(roomId);
                if (room) {
                    resolve(room);
                    return;
                }
            };

            checkRoom();

            const timeoutId = setTimeout(() => {
                resolve(null);
            }, timeout);

            const onNewRoom = (room: Room) => {
                if (room.roomId === roomId) {
                    clearTimeout(timeoutId);
                    this.off('newRoom', onNewRoom);
                    resolve(room);
                }
            };

            this.on('newRoom', onNewRoom);
        });
    }

    private async awaitOurMembership(roomId: string, membership: string = 'join', timeout: number = 5000): Promise<void> {
        return new Promise((resolve, reject) => {
            const checkMembership = () => {
                const room = this.client?.getRoom(roomId);
                const myMembership = room?.getMyMembership();
                return myMembership === membership;
            };

            if (checkMembership()) {
                resolve();
                return;
            }

            const timeoutId = setTimeout(() => {
                this.client?.off('sync' as any, onSync);
                reject(new Error(`Timeout waiting for our membership in room ${roomId}`));
            }, timeout);

            const onSync = () => {
                if (checkMembership()) {
                    clearTimeout(timeoutId);
                    this.client?.off('sync' as any, onSync);
                    resolve();
                }
            };

            this.client?.on('sync' as any, onSync);
        });
    }

    private async awaitRoomEncryption(roomId: string, timeout: number = 8000): Promise<void> {
        if (!this.client) throw new Error('Client not initialized');

        const isEncrypted = () => {
            const room = this.client?.getRoom(roomId);
            return !!room?.hasEncryptionStateEvent();
        };

        if (isEncrypted()) {
            this.roomsPendingEncryption.delete(roomId);
            return;
        }

        return new Promise((resolve) => {
            const timeoutId = setTimeout(() => {
                this.client?.off('RoomState.events' as any, onRoomStateEvent);
                this.client?.off('sync' as any, onSync);
                logger.warn('[WEB CHAT] Timeout waiting for room encryption to be ready', this.encCtx({ event: 'room_encryption_timeout', roomId }));
                resolve();
            }, timeout);

            const onRoomStateEvent = (event: MatrixEvent) => {
                if (event.getRoomId() !== roomId) return;
                if (event.getType() !== EventType.RoomEncryption) return;
                if (!isEncrypted()) return;
                clearTimeout(timeoutId);
                this.client?.off('RoomState.events' as any, onRoomStateEvent);
                this.client?.off('sync' as any, onSync);
                this.roomsPendingEncryption.delete(roomId);
                resolve();
            };

            const onSync = () => {
                if (!isEncrypted()) return;
                clearTimeout(timeoutId);
                this.client?.off('RoomState.events' as any, onRoomStateEvent);
                this.client?.off('sync' as any, onSync);
                this.roomsPendingEncryption.delete(roomId);
                resolve();
            };

            this.client?.on('RoomState.events' as any, onRoomStateEvent);
            this.client?.on('sync' as any, onSync);
        });
    }

    prepareToEncrypt(roomId: string): void {
        if (!this.client) return;
        const room = this.client.getRoom(roomId);
        if (!room || !room.hasEncryptionStateEvent()) return;
        const crypto = this.client.getCrypto();
        if (crypto) {
            crypto.prepareToEncrypt(room);
        }
    }

    private async ensureRoomEncryptionReady(roomId: string): Promise<void> {
        if (!this.roomsPendingEncryption.has(roomId)) return;

        const room = this.client?.getRoom(roomId);
        if (room?.hasEncryptionStateEvent()) {
            this.roomsPendingEncryption.delete(roomId);
            return;
        }

        await this.awaitRoomEncryption(roomId);

        const updatedRoom = this.client?.getRoom(roomId);
        if (updatedRoom?.hasEncryptionStateEvent()) {
            this.roomsPendingEncryption.delete(roomId);
            return;
        }

        throw new Error(`Room encryption not ready for ${roomId}`);
    }

    /**
     * Waits until all users invited at room creation are joined (per local state).
     * Returns true when this call had to track pending members — i.e. this is the
     * first send in a freshly created room and device keys may not be known yet.
     */
    private async ensureRoomMembersReady(roomId: string): Promise<boolean> {
        const pending = this.roomPendingMemberIds.get(roomId);
        if (!pending || pending.size === 0) {
            this.roomPendingMemberIds.delete(roomId);
            return false;
        }

        const isReady = () => {
            const room = this.client?.getRoom(roomId);
            if (!room) return false;
            for (const userId of pending) {
                const member = room.getMember(userId);
                if (!member || member.membership !== 'join') {
                    return false;
                }
            }
            return true;
        };

        if (isReady()) {
            this.roomPendingMemberIds.delete(roomId);
            return true;
        }

        await new Promise((resolve) => {
            const finish = () => {
                clearTimeout(timeoutId);
                this.client?.off('RoomMember.membership' as any, onMember);
                this.client?.off('sync' as any, onSync);
                this.roomPendingMemberIds.delete(roomId);
                resolve(null);
            };

            const timeoutId = setTimeout(() => {
                logger.warn('[WEB CHAT] Timeout waiting for room members to join', this.encCtx({ event: 'room_members_timeout', roomId, pendingUserIds: Array.from(pending) }));
                finish();
            }, 15_000);

            const onMember = (_event: MatrixEvent, member: any) => {
                if (member?.roomId !== roomId) return;
                if (member?.membership === 'join' && pending.has(member.userId)) {
                    pending.delete(member.userId);
                }
                if (pending.size > 0 && !isReady()) return;
                finish();
            };

            const onSync = () => {
                if (!isReady()) return;
                finish();
            };

            this.client?.on('RoomMember.membership' as any, onMember);
            this.client?.on('sync' as any, onSync);
        });

        return true;
    }

    /**
     * Forces a device-key download for the room's current members before the
     * first message in a freshly created room is encrypted. Without this the
     * first megolm key share can race the initial /keys/query for a brand-new
     * peer: their device misses the session at index 0 and the first message
     * stays permanently undecryptable on it, while later messages (shared at a
     * higher ratchet index) decrypt fine.
     *
     * Bypasses the downloadedKeys/downloadingKeys caches on purpose — an earlier
     * fire-and-forget download may have run before the invited members joined
     * and cached a partial member set.
     */
    private async ensureMemberDeviceKeysForSend(roomId: string): Promise<void> {
        const client = this.client;
        if (!client) return;

        const crypto = client.getCrypto();
        const room = client.getRoom(roomId);
        if (!crypto || !room || !room.hasEncryptionStateEvent()) return;

        const myUserId = client.getUserId();
        const userIds = room.getJoinedMembers()
            .map(m => m.userId)
            .filter(uid => uid !== myUserId);
        if (userIds.length === 0) return;

        try {
            await crypto.getUserDeviceInfo(userIds, true);
            this.downloadedKeys.add(roomId);
        } catch (error) {
            // Don't block the send — the SDK will still share keys on a
            // best-effort basis; worst case matches the previous behaviour.
            logger.warn(`[WEB CHAT] Failed to download member device keys before first send: ${error instanceof Error ? error.message : String(error)}`, this.encCtx({ event: 'first_send_device_keys_error', roomId, error: error instanceof Error ? error.message : String(error) }));
        }
    }



    getChildRooms(parentRoomId: string): Room[] {
        if (!this.client) return [];

        const parentRoom = this.getRoom(parentRoomId);
        if (!parentRoom) return [];

        const childRooms: Room[] = [];

        try {
            const childEvents = parentRoom.currentState.getStateEvents('m.room.child');

            childEvents.forEach(event => {
                const childRoomId = event.getStateKey();
                if (childRoomId) {
                    const childRoom = this.getRoom(childRoomId);
                    if (childRoom) {
                        childRooms.push(childRoom);
                    }
                }
            });
        } catch (error) {
            logger.error(`[WEB CHAT] Error getting child rooms: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'get_child_rooms_error', roomId: parentRoomId, error: error instanceof Error ? error.message : String(error) });
        }

        return childRooms;
    }

    getParentRoom(childRoomId: string): Room | null {
        if (!this.client) return null;

        const childRoom = this.getRoom(childRoomId);
        if (!childRoom) return null;

        try {
            const parentEvents = childRoom.currentState.getStateEvents('m.room.parent');

            if (parentEvents.length > 0) {
                const parentRoomId = parentEvents[0].getStateKey();
                if (parentRoomId) {
                    return this.getRoom(parentRoomId);
                }
            }
        } catch (error) {
            logger.error(`[WEB CHAT] Error getting parent room: ${error instanceof Error ? error.message : String(error)}`, { source: 'chat', event: 'get_parent_room_error', roomId: childRoomId, error: error instanceof Error ? error.message : String(error) });
        }

        return null;
    }

    private async checkRustCryptoDbExists(prefix: string): Promise<boolean> {
        try {
            if (typeof indexedDB.databases === 'function') {
                const databases = await indexedDB.databases();
                return databases.some(db => db.name?.startsWith(prefix));
            }
            // Fallback: try opening the DB and check if onupgradeneeded fires (meaning it's new/empty)
            return new Promise<boolean>((resolve) => {
                const req = indexedDB.open(prefix);
                req.onupgradeneeded = () => {
                    // DB didn't exist — abort so we don't actually create it
                    req.transaction?.abort();
                    resolve(false);
                };
                req.onsuccess = () => {
                    req.result.close();
                    resolve(true);
                };
                req.onerror = () => {
                    resolve(false);
                };
            });
        } catch {
            return false;
        }
    }

    /** Runs after the client is stopped, which closes the Rust crypto store so it can be deleted. */
    private async discardUnpublishedDevice(client: MatrixClient, stores: UnpublishedDeviceStores): Promise<void> {
        localStorage.removeItem(`mx_crypto_initialised:${stores.storeId}`);
        removeDeviceId(stores.tenant, stores.authUserId);
        removeDeviceId(stores.tenant, stores.matrixUserId);
        try {
            await client.clearStores({ cryptoDatabasePrefix: stores.cryptoDbPrefix });
        } catch (error) {
            logger.warn('[WEB CHAT] Failed to clear stores of the unpublished device', this.encCtx({
                event: 'own_device_keys_store_clear_failed',
                error: error instanceof Error ? error.message : String(error),
            }));
        }
        await this.deleteIndexedDBByPrefix(stores.cryptoDbPrefix);
    }

    private async deleteIndexedDBByPrefix(prefix: string): Promise<void> {
        try {
            if (typeof indexedDB.databases === 'function') {
                const databases = await indexedDB.databases();
                const toDelete = databases.filter(db => db.name?.startsWith(prefix));
                await Promise.all(
                    toDelete.map(db => new Promise<void>((resolve, reject) => {
                        const req = indexedDB.deleteDatabase(db.name!);
                        req.onsuccess = () => resolve();
                        req.onerror = () => reject(req.error);
                    }))
                );
            }
        } catch (error) {
            logger.warn('[WEB CHAT] Failed to delete IndexedDB by prefix', this.encCtx({
                event: 'delete_indexeddb_failed',
                prefix,
                error: error instanceof Error ? error.message : String(error),
            }));
        }
    }

    async logout(): Promise<void> {
        if (this.logoutInFlight) {
            return this.logoutInFlight;
        }

        const logout = this.performLogout().finally(() => {
            if (this.logoutInFlight === logout) {
                this.logoutInFlight = null;
            }
        });
        this.logoutInFlight = logout;
        return logout;
    }

    private async performLogout(): Promise<void> {
        const tenant = store.getState().auth.tenant;
        const authUserId = store.getState().auth.userId;
        const staleInitialization = this.invalidateInitialization();

        this.cachedSecretStorageKey = null;
        this.matrixRefreshToken = null;
        this.clearTokenRefreshTimeout();
        this.tokenRefreshInFlight = null;
        this.tokenExpiryRecoveryInFlight = null;

        // Soft logout deliberately keeps the Matrix device and its crypto
        // stores. Senders can continue sharing room keys with this device while
        // the user is signed out, and the next login can decrypt queued events.
        // Do not call logout(), clearStores(), or removeDeviceId() here.
        this.stopActiveClient(
            '[WEB CHAT] Failed to stop Matrix client during logout',
            'matrix_stop_client_failed'
        );

        // stopClient() closes Rust Crypto and its IndexedDB connection without
        // deleting the persisted keys. Release the cross-tab lock afterwards.
        if (tenant && authUserId) {
            try {
                releaseMatrixSessionLock({ tenant, userId: authUserId });
            } catch (error) {
                logger.warn('[WEB CHAT] Failed to release Matrix session lock during logout', this.encCtx({
                    event: 'matrix_logout_lock_release_failed',
                    error: error instanceof Error ? error.message : String(error),
                }));
            }
        }

        try {
            localStorage.removeItem(MatrixService.SELECTED_ROOM_STORAGE_KEY);
            removeMatrixToken(tenant, authUserId);
        } catch (error) {
            logger.warn('[WEB CHAT] Failed to clear persisted Matrix session metadata during logout', this.encCtx({
                event: 'matrix_logout_storage_cleanup_failed',
                error: error instanceof Error ? error.message : String(error),
            }));
        }

        try {
            slidingSyncManager.stop();
        } catch (error) {
            logger.warn('[WEB CHAT] Failed to stop sliding sync during logout', this.encCtx({
                event: 'matrix_logout_sliding_sync_stop_failed',
                error: error instanceof Error ? error.message : String(error),
            }));
        }

        // This is the security boundary: clear observable state before any
        // asynchronous cleanup so another account can never inherit it.
        this.clearInMemorySession('active');
        store.dispatch(setMatrixSession({
            accessToken: null,
            refreshToken: null,
            expiresAtMs: null,
        }));
        this.emitStateChange();

        // A superseded initialization cleans up its captured client in its own
        // catch path. Waiting for it here could block logout forever on an SDK
        // or IndexedDB operation which does not support cancellation.
        void staleInitialization?.catch(() => undefined);

        await mediaCacheService.clearCache();
        logger.info('[WEB CHAT] Soft logout completed; Matrix device and crypto stores preserved', this.encCtx({
            event: 'matrix_soft_logout_complete',
        }));
    }

  private async loadMembersForIncompleteRooms() {
    for (const room of this.chatState.rooms) {
      const expectedCount =
        (room.getJoinedMemberCount() || 0) + (room.getInvitedMemberCount() || 0);
      const currentCount = room.getMembers().length;
      if (currentCount < expectedCount && this.isDirectMessageRoom(room)) {
        try {
          await room.loadMembersIfNeeded();
          room.recalculate();
        } catch (error) {
          console.error(`Failed to load members for room ${room.roomId}:`, error);
        }
      }
    }

    this.emitStateChange();
  }

}

export const matrixService = new MatrixService();

if (typeof window !== 'undefined') {
    window.__matrixCryptoStatus = () => matrixService.getCryptoRuntimeStatus();
}
